// =============================================================================
// client/src/pages/OrderLinesEditorPage.js - Main order editing experience
// =============================================================================
// This is the primary page opened from HubSpot via a popup or from the admin sidebar.
//
// CORE FLOW:
//   1. Boot useEffect resolves URL params → loads/creates an order
//   2. syncHubSpotLines() imports line items from the HubSpot deal into the order
//   3. User adds/edits/removes lines via the right-side product picker drawer
//   4. User substitutes products via the inline substitute finder panel
//   5. On Submit → hubspotSubmitOrder() pushes lines back to the HubSpot deal
//
// URL PARAMS (all optional, set by HubSpot popup launch or internal links):
//   ?hubspotOrderId=  - HubSpot line item group ID (creates/loads order by this ID)
//   ?dealId=          - HubSpot deal ID (linked to order for CRM push)
//   ?storeId=         - site/store ID (overrides user's default site for stock display)
//   ?orderId=         - load a specific order by internal OrderId
//
// ACCESS MODES:
//   isStoreMode = true   - opened via HubSpot popup or storeId URL param
//                          Hides full sidebar nav; shows submit confirmation + window.close()
//   isStoreMode = false  - opened from admin sidebar; full navigation available
//
// LAYOUT:
//   ┌─ Order summary bar (order number, site, total, submit button) ───────┐
//   │  Order lines table (pack headers + children, substitute badges)      │
//   │  Substitute finder panel (inline, expands under selected line)       │
//   └───────────────────────────────────────────────────────────────────────┘
//   ┌─ Product picker drawer (slides in from right, 50% width) ────────────┐
//   │  Tab: Catalog (search/filter, add products/packs)                    │
//   │  Tab: Substitute (recommended subs, algorithm search, browse)        │
//   └───────────────────────────────────────────────────────────────────────┘
//
// STATE GROUPS (see comments in each block below):
//   - Boot/session: currentUser, sites, loading, statusMsg
//   - Order: order, orderLines
//   - Product catalog: products, categories, pagination, filter state
//   - Product picker drawer: showProductPicker, drawerTab, rowQtys
//   - Substitute finder: subFinderLineId, subResults, subCriteria, recommendedSubs
//   - Inline product change: editingProductLineId, productSearchText, pickerPos
//   - Pack substitution popup: packSubPopup, subReasons, selectedReasonId
//   - Submit flow: submitting, showSubmitConfirm, hubspotSyncing
//
// TO ADD A NEW ACTION (e.g. "Copy Order"):
//   1. Add a handler function (handleCopyOrder) in the handlers section
//   2. Add an api.js export
//   3. Add a button in the order summary bar JSX section
//
// TO ADD A NEW LINE TABLE COLUMN:
//   1. Add the <th> in the table header
//   2. Add the <td> in the PACK HEADER ROW block AND the PACK CHILD/STANDALONE ROW block
// =============================================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  getProducts, getSites, getProductCategories,
  getProductLifecycleStages, getProductFreeFromTags, getProductDietaryTags,
  getUsers, getOrder, createOrder, updateOrder, addOrderLine, addPackToOrder, updateOrderLine,
  deleteOrderLine, findSubstitutes, getProductSubstitutions, hubspotImportOrder, hubspotSubmitOrder,
  getSubstitutionReasons, applyOrderDiscount, hubspotPreviewLines,
  startAppSession, heartbeatAppSession,
} from '../api';
import {
  SUBMIT_STATES,
  buildSubmitFailureMessage,
  buildSubmitSuccessMessage,
  canStartSubmit,
  getSubmitButtonLabel,
  isSubmitInFlight,
} from './orderSubmitState';
import { buildLaunchSource, getHeartbeatMs, getOrCreateAppSessionId, shouldRegisterAppSession } from '../telemetrySession';
import { resolveOrderEditorSiteId } from '../orderLaunchSite';
import { lineAmounts, orderTotals, round2 } from '../orderMath';

// Colour-coded allergen badges - add new allergens here to get a badge colour
const ALLERGEN_COLOURS = {
  Gluten: '#d97706',      Milk: '#2563eb',      Eggs: '#dc2626',
  Fish: '#0891b2',        Crustaceans: '#e11d48', Soya: '#65a30d',
  Sesame: '#9333ea',      Celery: '#059669',     Nuts: '#b45309',
  Peanuts: '#92400e',     Mustard: '#ca8a04',    Lupin: '#7c3aed',
  Molluscs: '#0d9488',    Sulphites: '#6366f1',
};

// VAT helpers - A0 = 0% VAT, A1 = 20% VAT; unknown treated as standard 20%
const getVatRate = (taxCode) => taxCode === 'A0' ? 0 : 0.20;
const calcExVat = (unitPrice, taxCode) => parseFloat(unitPrice || 0) / (1 + getVatRate(taxCode));

export default function OrderLinesEditorPage() {

  const [searchParams] = useSearchParams();
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();

  // ---- URL launch params ----
  // Set by HubSpot popup URL or internal navigation
  const urlHubspotOrderId = searchParams.get('hubspotOrderId');
  const urlDealId = searchParams.get('dealId');
  const urlStoreId    = searchParams.get('storeId');    // site for stock display
  const urlOrderId    = searchParams.get('orderId');    // load existing order directly

  // Store mode: opened via HubSpot popup (no sidebar, confirm on submit)
  const isStoreMode = !!(urlHubspotOrderId || urlStoreId);

  // ---- Session state ----
  const [currentUser, setCurrentUser] = useState(null);  // logged-in user (from /api/users)
  const [sites, setSites] = useState([]);                 // all active sites (for display)

  // ---- Order state ----
  const [order, setOrder] = useState(null);           // order header object
  const [orderLines, setOrderLines] = useState([]);   // array of enriched line objects
  const [loading, setLoading] = useState(true);       // boot loading spinner
  const [statusMsg, setStatusMsg] = useState(null);   // { type: 'success'|'error'|'info', text }

  // ---- Product catalog state (for the picker drawer) ----
  const [products, setProducts] = useState([]);           // current page of products/packs
  const [categories, setCategories] = useState([]);       // all categories (filter dropdown)
  const [allFreeFromTags, setAllFreeFromTags] = useState([]);
  const [allDietaryTags, setAllDietaryTags] = useState([]);
  const [lifecycleStages, setLifecycleStages] = useState([]);

  // ---- Pagination state ----
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ totalItems: 0, totalPages: 1, pageSize: 50 });

  // ---- Pack expansion state ----
  const [expandedPacks, setExpandedPacks] = useState(new Set());       // expanded in catalog drawer
  const [expandedOrderPacks, setExpandedOrderPacks] = useState(new Set()); // expanded in order table
  const [highlightedLineId, setHighlightedLineId] = useState(null); // set by warnings panel click

  // ---- Product filter state ----
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');           // 'all' | 'product' | 'pack'
  const [selectedFreeFrom, setSelectedFreeFrom] = useState([]);
  const [selectedDietary, setSelectedDietary] = useState([]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [promoOnly, setPromoOnly] = useState(false);
  const [showHsDebug, setShowHsDebug] = useState(false);
  const [hsPreview, setHsPreview] = useState(null);
  const [hsPreviewLoading, setHsPreviewLoading] = useState(false);

  // Resets all catalog search/filter fields - called on Close and after + Add
  const resetCatalogFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setLifecycleFilter('');
    setTypeFilter('all');
    setSelectedFreeFrom([]);
    setSelectedDietary([]);
    setInStockOnly(false);
    setPromoOnly(false);
  };

  // Fetch the live HubSpot payload preview from the server
  const loadHsPreview = async () => {
    if (!order) return;
    setHsPreviewLoading(true);
    try {
      const res = await hubspotPreviewLines(order.OrderId);
      setHsPreview(res.data);
    } catch (e) {
      setHsPreview({ error: e.response?.data?.error || e.message });
    }
    setHsPreviewLoading(false);
  };

  // Auto-refresh the debug panel whenever orderLines change (debounced 600 ms)
  const hsDebugTimerRef = useRef(null);
  useEffect(() => {
    if (!showHsDebug || !order) return;
    if (hsDebugTimerRef.current) clearTimeout(hsDebugTimerRef.current);
    hsDebugTimerRef.current = setTimeout(() => { loadHsPreview(); }, 600);
    return () => clearTimeout(hsDebugTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderLines, order?.DiscountPct, showHsDebug]);

  // ---- Product picker drawer ----
  const [showProductPicker, setShowProductPicker] = useState(false);  // controls drawer visibility
  const [drawerTab, setDrawerTab] = useState('catalog');              // 'catalog' | 'substitute'
  const [rowQtys, setRowQtys] = useState({});                         // qty input per catalog row

  // ---- Substitute finder (inline under a selected line) ----
  const [subFinderLineId, setSubFinderLineId] = useState(null);       // which line's sub panel is open
  const [subResults, setSubResults] = useState(null);                 // algorithmic search results
  const [subSearching, setSubSearching] = useState(false);
  const [subResultsFilter, setSubResultsFilter] = useState('');       // filter text inside results
  const [subCriteria, setSubCriteria] = useState({                    // criteria for /find-substitutes
    maxPriceDelta: 5, avoidAllergens: [], matchIngredients: [],
    inStockOnly: true, category: '',
  });
  const [subInnerTab, setSubInnerTab] = useState('recommended');      // 'recommended' | 'search' | 'browse'
  const [recommendedSubs, setRecommendedSubs] = useState(null);       // Pimberly Sub1/Sub2 results
  const [recommendedLoading, setRecommendedLoading] = useState(false);
  const [browseSearch, setBrowseSearch] = useState('');               // browse-mode search text
  const [browseResults, setBrowseResults] = useState(null);
  const [browseLoading, setBrowseLoading] = useState(false);

  // ---- Inline product change (click ✏️ on a standalone line) ----
  const [editingProductLineId, setEditingProductLineId] = useState(null); // which line is in edit mode
  const [productSearchText, setProductSearchText] = useState('');         // text in inline picker input
  const productInputRef = useRef(null);
  const [pickerPos, setPickerPos] = useState(null);                       // { top, left, width } for floating dropdown

  // ---- Pack substitution price popup ----
  // Shown when substituting a pack child item - asks user to accept/reject price difference
  const [packSubPopup, setPackSubPopup] = useState(null); // { lineId, substitute, originalPrice, newPrice, priceDiff, line }
  const [subReasons, setSubReasons] = useState([]);
  const [selectedReasonId, setSelectedReasonId] = useState('');

  // ---- Submit flow ----
  const [submitting, setSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState(SUBMIT_STATES.IDLE);
  const [submitResult, setSubmitResult] = useState(null);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false); // post-submit confirmation in store mode
  const [hubspotSyncing, setHubspotSyncing] = useState(false);       // true while importing HubSpot lines

  // ---- Inline order number edit ----
  const [editingOrderNo, setEditingOrderNo] = useState(false);
  const [orderNoDraft, setOrderNoDraft] = useState('');

  // ---- Discount state ----
  // discountPct: the % value in the Discount % input (string for controlled input)
  // discountVal: the £ equivalent in the Discount £ input
  // discountTimerRef: debounces the save-to-server call while the user is typing
  const [discountPct, setDiscountPct] = useState('0');
  const [discountVal, setDiscountVal] = useState('0.00');
  const discountTimerRef = useRef(null);
  // lineDiscountTimers: debounces per-line discount saves keyed by LineId
  const lineDiscountTimers = useRef({});

  // Prevents the boot useEffect from running twice (React StrictMode double-invoke)
  const initiated = useRef(false);
  const appSessionIdRef = useRef(getOrCreateAppSessionId());
  const appSessionRegisteredRef = useRef(false);
  const [appSessionActive, setAppSessionActive] = useState(false);
  const accessMode = searchParams.get('CentralAdmin') === 'true' && searchParams.get('code') === '2026'
    ? 'admin'
    : isStoreMode ? 'store' : 'normal';

  const registerAppSessionForOrder = useCallback(async (targetOrder, user, siteId) => {
    if (!targetOrder?.OrderId || !shouldRegisterAppSession(appSessionRegisteredRef)) return;
    try {
      await startAppSession({
        SessionId: appSessionIdRef.current,
        SiteId: targetOrder.SiteId || siteId || null,
        UserId: user?.UserId || null,
        OrderId: targetOrder.OrderId,
        AccessMode: accessMode,
        LaunchSource: buildLaunchSource({ urlHubspotOrderId, urlDealId, urlOrderId, urlStoreId }),
      });
      setAppSessionActive(true);
    } catch (e) {
      console.warn('Telemetry session start failed:', e.response?.data?.error || e.message);
    }
  }, [accessMode, urlDealId, urlHubspotOrderId, urlOrderId, urlStoreId]);

  useEffect(() => {
    if (!appSessionActive || !order?.OrderId) return;
    const sendHeartbeat = () => {
      heartbeatAppSession({ SessionId: appSessionIdRef.current, OrderId: order.OrderId })
        .catch(e => console.warn('Telemetry heartbeat failed:', e.response?.data?.error || e.message));
    };
    const timer = setInterval(sendHeartbeat, getHeartbeatMs());

    const endSession = (event) => {
      if (event?.persisted) return;
      try {
        const body = JSON.stringify({ SessionId: appSessionIdRef.current });
        fetch('/api/telemetry/sessions/end', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(accessMode !== 'normal' ? { 'X-Access-Mode': accessMode } : {}) },
          body,
          keepalive: true,
        }).catch(() => {});
      } catch (_) {}
    };
    window.addEventListener('pagehide', endSession);
    return () => {
      clearInterval(timer);
      window.removeEventListener('pagehide', endSession);
    };
  }, [accessMode, appSessionActive, order?.OrderId]);

  // =====================================================
  // syncHubSpotLines - import deal line items from HubSpot into this order
  // =====================================================
  // Called automatically on boot (silent=true) and available for manual re-sync.
  // Flow: POST /api/hubspot/import/:orderId → then GET /api/orders/:id to refresh state.
  // The import API returns counts: { created, updated, unchanged, substitutionsApplied,
  //   skippedUnmatched, substitutionParseWarnings[], skipped[] }
  // Status message is shown unless silent=true AND there are no visible changes.
  //
  // TO TRIGGER A MANUAL RE-SYNC: call syncHubSpotLines(order) from a button handler.
  const syncHubSpotLines = useCallback(async (targetOrder, options = {}) => {
    // Resolve the HubSpot deal ID - prefer order's stored value, fallback to URL params
    const fallbackDealId = urlDealId || urlHubspotOrderId || null;
    const effectiveDealId = targetOrder?.HubSpotDealId || fallbackDealId;
    if (!targetOrder?.OrderId || !effectiveDealId) return null; // nothing to sync

    const { silent = false } = options;
    setHubspotSyncing(true);
    try {
      const importRes = await hubspotImportOrder(targetOrder.OrderId, effectiveDealId);
      // Refresh the full order (with enriched Lines) after the import
      const refreshed = await getOrder(targetOrder.OrderId);
      setOrder(refreshed.data);
      setOrderLines(refreshed.data.Lines || []);

      const result = importRes.data || {};
      const warnings = result.substitutionParseWarnings || [];
      const skipped = result.skipped || [];
      // Only show status if not silent, or if there are visible changes worth surfacing
      const hasVisibleChanges = result.created || result.updated || result.skippedUnmatched || result.substitutionsApplied || warnings.length;

      if (!silent || hasVisibleChanges) {
        const messageParts = [];
        if (result.created) messageParts.push(`imported ${result.created}`);
        if (result.updated) messageParts.push(`updated ${result.updated}`);
        if (result.unchanged) messageParts.push(`unchanged ${result.unchanged}`);
        if (result.substitutionsApplied) messageParts.push(`restored ${result.substitutionsApplied} substitution${result.substitutionsApplied === 1 ? '' : 's'}`);
        if (result.skippedUnmatched) messageParts.push(`skipped ${result.skippedUnmatched}`);
        if (warnings.length) messageParts.push(`${warnings.length} substitution warning${warnings.length === 1 ? '' : 's'}`);
        if (messageParts.length > 0) {
          const firstSkipped = skipped[0];
          const skippedDetail = firstSkipped
            ? ` First skipped item: ${firstSkipped.sku || firstSkipped.lineItemId} - ${firstSkipped.reason}.`
            : '';
          setStatusMsg({
            type: result.skippedUnmatched || warnings.length ? 'info' : 'success',
            text: `HubSpot sync: ${messageParts.join(', ')}.${skippedDetail}`,
          });
        }
      }

      return result;
    } catch (e) {
      setStatusMsg({ type: 'error', text: e.response?.data?.error || 'Failed to import HubSpot lines' });
      throw e;
    } finally {
      setHubspotSyncing(false);
    }
  }, [urlDealId, urlHubspotOrderId]);

  // =====================================================
  // 1. Boot: load reference data, resolve user, create/load order
  // =====================================================
  // Runs once on mount (guarded by initiated ref).
  // Steps:
  //   a) Parallel-load all reference data (sites, categories, tags, users, sub reasons)
  //   b) Retry once after 2s - guards against cold server startup before DB is ready
  //   c) Resolve currentUser (default = first user) and siteId (URL param → user's site)
  //   d) If ?orderId is in URL → load that order, then silent HubSpot sync
  //   e) Otherwise → createNewOrder() which calls POST /api/orders (idempotent by HubSpotDealId)
  //      then silent HubSpot sync
  //
  // createNewOrder is defined as an inner function to keep the async IIFE readable.
  // TO LOAD ADDITIONAL REFERENCE DATA ON BOOT: add the API call inside loadRefData().
  useEffect(() => {
    if (initiated.current) return;
    initiated.current = true;

    (async () => {
      // Parallel load all reference data needed for the product catalog and filters
      const loadRefData = async () => {
        const [sitesRes, catsRes, lsRes, ffRes, dtRes, usersRes, reasonsRes] = await Promise.all([
          getSites(), getProductCategories(), getProductLifecycleStages(),
          getProductFreeFromTags(), getProductDietaryTags(), getUsers(),
          getSubstitutionReasons().catch(() => ({ data: [] }))
        ]);
        return { sitesRes, catsRes, lsRes, ffRes, dtRes, usersRes, reasonsRes };
      };

      try {
        // Try loading reference data; retry once after 2s if server isn't ready
        let refData;
        try {
          refData = await loadRefData();
        } catch (firstErr) {
          console.warn('Boot: initial load failed, retrying in 2s...', firstErr.message);
          await new Promise(r => setTimeout(r, 2000));
          refData = await loadRefData();
        }

        const { sitesRes, catsRes, lsRes, ffRes, dtRes, usersRes, reasonsRes } = refData;
        const sitesList = sitesRes.data;
        setSubReasons(reasonsRes.data || []);
        const catsList = catsRes.data;
        const usersList = usersRes.data;
        setSites(sitesList);
        setCategories(catsList);
        setLifecycleStages(lsRes.data);
        setAllFreeFromTags(ffRes.data);
        setAllDietaryTags(dtRes.data);

        // Resolve user - default to first user (no SSO in store mode)
        let user = null;
        if (usersList.length > 0) {
          user = usersList[0];
        }
        setCurrentUser(user);

        const siteResolution = resolveOrderEditorSiteId({
          urlStoreId,
          userSiteId: user?.SiteId,
          isHubSpotLaunch: !!(urlHubspotOrderId || urlDealId),
        });
        const siteId = siteResolution.siteId;

        if (urlOrderId) {
          // Load existing order by OrderId
          try {
            const orderRes = await getOrder(parseInt(urlOrderId, 10));
            setOrder(orderRes.data);
            setOrderLines(orderRes.data.Lines || []);
            registerAppSessionForOrder(orderRes.data, user, siteId);
            setStatusMsg({ type: 'success', text: `Loaded Order ${orderRes.data.OrderNumber}` });
            // Silent sync - HubSpot errors here are connectivity issues, not load failures
            try {
              await syncHubSpotLines(orderRes.data, { silent: true });
            } catch (hsErr) {
              const hsMsg = hsErr.response?.data?.error || hsErr.message || 'HubSpot sync unavailable';
              setStatusMsg({ type: 'info', text: `Order loaded. HubSpot sync skipped: ${hsMsg}` });
            }
          } catch (e) {
            setStatusMsg({ type: 'error', text: 'Order not found: ' + (e.response?.data?.error || e.message) });
          }
        } else {
          // No orderId - create or find existing order by HubSpotOrderId/DealId
          if (siteResolution.error) {
            setStatusMsg({ type: 'error', text: siteResolution.error });
            return;
          }
          await createNewOrder(user, siteId);
        }
      } catch (e) {
        console.error(e);
        setStatusMsg({ type: 'error', text: 'Failed to initialise page' });
      }
      setLoading(false);
    })();

    // Inner async function - creates a new order (or loads existing if POST returns 200)
    // POST /api/orders is idempotent: if an order for this HubSpotDealId already exists,
    // it returns HTTP 200 with the existing order. HTTP 201 = brand-new order.
    async function createNewOrder(user, siteId) {
      try {
        const res = await createOrder({
          SiteId: siteId,
          HubSpotDealId: urlDealId || urlHubspotOrderId || null,
          HubSpotOrderId: urlHubspotOrderId || null,
          UserId: user?.UserId || null,
          UserEmail: user?.Email || null,
          Notes: 'Created via Order Lines Editor',
        });
        const orderData = res.data;
        const isExisting = res.status === 200; // 200 = found existing, 201 = new
        // Reload with full enriched data (Lines, Site, stock etc.)
        const orderRes = await getOrder(orderData.OrderId);
        setOrder(orderRes.data);
        setOrderLines(orderRes.data.Lines || []);
        registerAppSessionForOrder(orderRes.data, user, siteId);
        setStatusMsg({
          type: 'success',
          text: isExisting
            ? `Loaded existing Order ${orderData.OrderNumber}`
            : `New Order ${orderData.OrderNumber} created`,
        });
        // Silent sync - errors here are HubSpot connectivity issues, not order creation failures
        try {
          await syncHubSpotLines(orderRes.data, { silent: true });
        } catch (hsErr) {
          // Don't overwrite the success message with a HubSpot sync error on boot;
          // the order loaded fine. Show a subtle info notice instead.
          const hsMsg = hsErr.response?.data?.error || hsErr.message || 'HubSpot sync unavailable';
          setStatusMsg({ type: 'info', text: `Order loaded. HubSpot sync skipped: ${hsMsg}` });
        }
      } catch (e) {
        setStatusMsg({ type: 'error', text: 'Failed to create order: ' + (e.response?.data?.error || e.message) });
      }
    }
  }, [registerAppSessionForOrder, syncHubSpotLines, urlDealId, urlHubspotOrderId, urlOrderId, urlStoreId]);

  // =====================================================
  // 2. Fetch filtered products (debounced, abort-controlled)
  // =====================================================
  // Called on every filter/page change, with a 400ms debounce to avoid
  // hammering the API on each keystroke. Uses AbortController to cancel
  // the previous request if a new one fires before the old one resolves.
  //
  // TO ADD A NEW FILTER: add state above, include in params here, and add
  // a filter input in the product picker drawer JSX below.
  const fetchAbortRef = useRef(null);

  const fetchProducts = useCallback(async (requestedPage) => {
    // Cancel any in-flight request before starting a new one
    if (fetchAbortRef.current) fetchAbortRef.current.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    const p = requestedPage || page;
    const params = { page: p, pageSize: 50 };
    if (search) params.search = search;
    if (categoryFilter) params.category = categoryFilter;
    if (lifecycleFilter) params.lifecycleStage = lifecycleFilter;
    if (typeFilter !== 'all') params.type = typeFilter;                        // 'product' | 'pack'
    if (selectedFreeFrom.length > 0) params.freeFrom = selectedFreeFrom.join(',');
    if (selectedDietary.length > 0) params.dietary = selectedDietary.join(',');
    const stockSiteId = urlStoreId || currentUser?.SiteId;
    if (stockSiteId) params.siteId = stockSiteId;   // enriches results with StockAtSite
    if (inStockOnly) params.inStock = 'true';
    if (promoOnly) params.promoOnly = 'true';
    try {
      const res = await getProducts(params, controller.signal);
      if (res.data && res.data.pagination) {
        setProducts(res.data.data);
        setPagination(res.data.pagination);
      } else {
        // Legacy or non-paginated response
        setProducts(Array.isArray(res.data) ? res.data : []);
        setPagination({ totalItems: (Array.isArray(res.data) ? res.data : []).length, totalPages: 1, pageSize: 50 });
      }
    } catch (e) {
      // Ignore AbortController cancellations - they are expected on rapid filter changes
      if (e.name !== 'CanceledError' && e.code !== 'ERR_CANCELED') console.error(e);
    }
  }, [page, search, categoryFilter, lifecycleFilter, typeFilter, selectedFreeFrom, selectedDietary, inStockOnly, promoOnly, currentUser, urlStoreId]);

  // Reset to page 1 whenever any filter changes (so results start from the beginning)
  useEffect(() => {
    setPage(1);
  }, [search, categoryFilter, lifecycleFilter, typeFilter, selectedFreeFrom, selectedDietary, inStockOnly, promoOnly, currentUser]);

  // 400ms debounce - prevents a fetch on every keystroke in the search box
  useEffect(() => {
    const t = setTimeout(() => fetchProducts(), 400);
    return () => clearTimeout(t);
  }, [fetchProducts]);

  // =====================================================
  // 3. Reload order helper
  // =====================================================
  // Fetches the latest order (with enriched Lines) and updates state.
  // Called after every mutation (add/update/delete line, substitution, etc.)
  // to ensure the UI reflects the server-side state.
  const reloadOrder = useCallback(async () => {
    if (!order) return;
    try {
      const res = await getOrder(order.OrderId);
      setOrder(res.data);
      setOrderLines(res.data.Lines || []);
    } catch (e) { console.error(e); }
  }, [order]);

  // =====================================================
  // 4. Add line (product or pack)
  // =====================================================
  // Called when the user clicks "Add" in the product picker drawer.
  // Logic:
  //   - For products: if the product already exists as a standalone line → increment qty
  //   - For products: if new → optimistic temp line, then POST /api/orders/:id/lines
  //   - For packs: if the pack already exists → increment qty
  //   - For packs: if new → POST /api/orders/:id/lines/pack (explodes into child lines)
  //
  // Optimistic UI: temp lines are inserted immediately (_temp: true) so the user
  // sees instant feedback. reloadOrder() replaces them with server-side data.
  const handleAddProduct = async (product, qty) => {
    if (!order) return;
    const addQty = qty || 1;
    setRowQtys(prev => ({ ...prev, [product.ProductId]: '' })); // clear qty input

    if (product._type !== 'pack') {
      // Check if this product already exists as a standalone line (not a pack child)
      const existingLine = orderLines.find(l => l.ProductId === product.ProductId && !l.ParentPackCode);
      if (existingLine && !existingLine._temp) {
        // Increment quantity on existing line instead of duplicating
        const newQty = (existingLine.Quantity || 1) + addQty;
        setOrderLines(prev => prev.map(l => l.LineId === existingLine.LineId ? { ...l, Quantity: newQty } : l));
        setStatusMsg({ type: 'success', text: `Updated ${product.ProductName} qty to ${newQty}` });
        try {
          await updateOrderLine(order.OrderId, existingLine.LineId, { Quantity: newQty });
          reloadOrder();
        } catch (e) {
          setStatusMsg({ type: 'error', text: e.response?.data?.error || e.message });
          reloadOrder();
        }
        return;
      }
      // No existing line - add a temporary optimistic line while the API call fires
      const tempId = -Date.now(); // negative ID signals a temporary line
      setOrderLines(prev => [...prev, {
        LineId: tempId, ProductId: product.ProductId, Quantity: addQty,
        UnitPrice: product.UnitPrice, Product: product, StockAtSite: product.StockAtSite,
        _temp: true,
      }]);
      setStatusMsg({ type: 'success', text: `Added ${addQty}× ${product.ProductName}` });
    } else {
      // Pack: check if already in the order
      const existingPackLine = orderLines.find(l => l.PackId === product.PackId && !l.ParentPackCode);
      if (existingPackLine && !existingPackLine._temp) {
        const newQty = (existingPackLine.Quantity || 1) + addQty;
        setOrderLines(prev => prev.map(l => l.LineId === existingPackLine.LineId ? { ...l, Quantity: newQty } : l));
        setStatusMsg({ type: 'success', text: `Updated "${product.ProductName}" qty to ${newQty}` });
        try {
          await updateOrderLine(order.OrderId, existingPackLine.LineId, { Quantity: newQty });
          reloadOrder();
        } catch (e) {
          setStatusMsg({ type: 'error', text: e.response?.data?.error || e.message });
          reloadOrder();
        }
        return;
      }
      setStatusMsg({ type: 'info', text: `Adding pack "${product.ProductName}"…` });
    }
    try {
      if (product._type === 'pack') {
        // POST /api/orders/:id/lines/pack - server explodes pack into child product lines
        await addPackToOrder(order.OrderId, product.PackId, addQty);
        setStatusMsg({ type: 'success', text: `Added pack "${product.ProductName}"` });
      } else {
        await addOrderLine(order.OrderId, {
          ProductId: product.ProductId,
          Quantity: addQty,
          UnitPrice: product.UnitPrice,
        });
      }
      reloadOrder();
    } catch (e) {
      setStatusMsg({ type: 'error', text: e.response?.data?.error || e.message });
      reloadOrder();
    }
  };

  // =====================================================
  // 5. Update line quantity
  // =====================================================
  // Optimistic update: UI is changed immediately; API call fires in background.
  // Pack headers: updating a pack header qty also updates all pack children
  // so the entire pack scales together.
  const handleQtyChange = async (lineId, newQty) => {
    if (!order || newQty < 1) return;
    const line = orderLines.find(l => l.LineId === lineId);
    const lineProductBlocked = line?.Product && (line.Product.IsActive === false || line.Product.EStop === true);
    const linePackBlocked = line?.Pack && line.Pack.IsActive === false;
    if ((lineProductBlocked || linePackBlocked) && newQty > line.Quantity) return;
    // A pack header has PackId set, no ParentPackCode, and no ProductId of its own
    const isPackHeader = line && line.PackId && !line.ParentPackCode && !line.ProductId;
    // Optimistic update - update header and all children immediately
    setOrderLines(prev => {
      const updated = prev.map(l => l.LineId === lineId ? { ...l, Quantity: newQty } : l);
      if (isPackHeader) {
        return updated.map(l => l.PackHeaderLineId === lineId ? { ...l, Quantity: newQty } : l);
      }
      return updated;
    });
    try {
      const promises = [updateOrderLine(order.OrderId, lineId, { Quantity: newQty })];
      if (isPackHeader) {
        // Also update each child line in parallel so the pack totals stay consistent
        const children = orderLines.filter(l => l.PackHeaderLineId === lineId);
        children.forEach(child => {
          promises.push(updateOrderLine(order.OrderId, child.LineId, { Quantity: newQty }));
        });
      }
      await Promise.all(promises);
      await reloadOrder();
    } catch (e) { console.error(e); reloadOrder(); }
  };

  // =====================================================
  // 5b. Update line total (back-calculates unit price from inc-VAT total)
  // =====================================================
  // newTotal is the inc-VAT discounted total the user typed.
  // Back-calculates to gross UnitPrice (inc-VAT) stored in the DB.
  const handleLineTotalChange = async (lineId, newTotal) => {
    if (!order) return;
    const line = orderLines.find(l => l.LineId === lineId);
    if (!line || line.Quantity < 1) return;
    // newTotal = qty × UnitPrice × (1 - discPct/100)
    const disc = (parseFloat(line.DiscountPct) || 0) / 100;
    const effectiveQty = line.Quantity * (1 - disc);
    const newUnitPrice = effectiveQty > 0 ? Math.round(newTotal / effectiveQty * 100) / 100 : 0;
    setOrderLines(prev => prev.map(l => l.LineId === lineId ? { ...l, UnitPrice: newUnitPrice } : l));
    try {
      await updateOrderLine(order.OrderId, lineId, { UnitPrice: newUnitPrice });
      await reloadOrder();
    } catch (e) { console.error(e); reloadOrder(); }
  };

  // =====================================================
  // 6. Remove line
  // =====================================================
  // Removes a line (or entire pack if it's a pack header) optimistically.
  // Server-side, deleting a pack header also cascades to delete all child lines.
  // If the deleted line had the substitute finder open, close it.
  const handleRemoveLine = async (lineId) => {
    if (!order) return;
    // Optimistic: remove from UI instantly
    setOrderLines(prev => prev.filter(l => l.LineId !== lineId));
    if (subFinderLineId === lineId) { setSubFinderLineId(null); setSubResults(null); }
    try {
      await deleteOrderLine(order.OrderId, lineId);
      reloadOrder();
    } catch (e) {
      console.error(e);
      reloadOrder();
    }
  };

  // =====================================================
  // 7. Change product on a line (inline ✏️ dropdown)
  // =====================================================
  // For standalone lines: replaces the product and updates UnitPrice to match.
  // Pack child lines use SubstitutionCode instead (handled in handlePickSubstitute).
  // TO ADD: logic for updating pack children when a pack header's product changes.
  const handleChangeLineProduct = async (lineId, newProductId) => {
    if (!order || !newProductId) return;
    const product = products.find(p => p.ProductId === parseInt(newProductId));
    if (!product) return;
    try {
      await updateOrderLine(order.OrderId, lineId, {
        ProductId: product.ProductId,
        UnitPrice: product.UnitPrice,
      });
      await reloadOrder();
    } catch (e) { console.error(e); }
  };

  // =====================================================
  // 8. Substitute finder - open, search, pick
  // =====================================================
  // openSubFinder: toggles the inline substitute panel for a line.
  //   - Opens the product drawer on the 'substitute' tab
  //   - Auto-loads Pimberly Sub1/Sub2 recommendations via getProductSubstitutions()
  //   - Pre-fills subCriteria with the line's category
  //
  // handleFindSubs: runs the algorithmic search (POST /api/products/:id/find-substitutes)
  //   with the current subCriteria (price delta, allergen avoidance, stock filter etc.)
  //
  // handlePickSubstitute: applies the chosen substitute to a line:
  //   - Pack child lines → shows the price diff popup (handlePackSubChoice) first
  //   - Standalone lines → replaces product directly (optimistic + API)
  //
  // TO ADD A NEW SUBSTITUTE SEARCH CRITERION: add to subCriteria state, add input
  // in the drawer JSX, and include in the handleFindSubs params object.
  const openSubFinder = (line) => {
    if (subFinderLineId === line.LineId) {
      // Toggle off if already open for this line
      setSubFinderLineId(null);
      setSubResults(null);
      setRecommendedSubs(null);
      return;
    }
    setSubFinderLineId(line.LineId);
    setSubResults(null);
    setRecommendedSubs(null);
    setBrowseSearch('');
    setBrowseResults(null);
    setSubInnerTab('recommended');
    const prod = line.Product;
    setSubCriteria({
      maxPriceDelta: 5,
      avoidAllergens: [],
      matchIngredients: [],
      inStockOnly: true,
      category: prod?.Category || '',
    });
    setShowProductPicker(true);
    setDrawerTab('substitute');
    // Auto-load Pimberly-defined Sub1/Sub2 recommendations immediately
    if (prod?.ProductId) {
      setRecommendedLoading(true);
      const stockSiteId = urlStoreId || currentUser?.SiteId || null;
      getProductSubstitutions(prod.ProductId, stockSiteId)
        .then(res => setRecommendedSubs(res.data))
        .catch(() => setRecommendedSubs([]))
        .finally(() => setRecommendedLoading(false));
    }
  };

  const handleFindSubs = async (productId) => {
    setSubSearching(true);
    try {
      const stockSiteId = urlStoreId || currentUser?.SiteId || null;
      const res = await findSubstitutes(productId, {
        siteId: stockSiteId,
        ...subCriteria,
      });
      setSubResults(res.data);
    } catch (e) { console.error(e); }
    setSubSearching(false);
  };

  // handlePickSubstitute: branch on pack child vs standalone
  const handlePickSubstitute = async (lineId, substitute) => {
    if (!order) return;
    const line = orderLines.find(l => l.LineId === lineId);
    try {
      if (line && line.ParentPackCode) {
        // ---- PACK CHILD: show price diff popup first ----
        // User must choose to "Accept" (adjusts pack price) or "Keep original" (no price change)
        const originalProduct = line.Product;
        const originalPrice = originalProduct ? originalProduct.UnitPrice : line.UnitPrice;
        const newPrice = substitute.UnitPrice || 0;
        const priceDiff = newPrice - originalPrice;

        setSelectedReasonId('');
        setPackSubPopup({ lineId, substitute, originalPrice, newPrice, priceDiff, line });
        return;
      } else {
        // ---- STANDALONE: replace product directly ----
        setOrderLines(prev => prev.map(l => l.LineId === lineId ? {
          ...l, ProductId: substitute.ProductId, UnitPrice: substitute.UnitPrice,
          Product: substitute, StockAtSite: substitute.StockAtSite,
        } : l));
        setSubFinderLineId(null);
        setSubResults(null);
        setStatusMsg({ type: 'success', text: `Replaced with ${substitute.ProductName}` });
        updateOrderLine(order.OrderId, lineId, { ProductId: substitute.ProductId, UnitPrice: substitute.UnitPrice, SubstitutionPriceAccepted: 1 })
          .then(() => reloadOrder()).catch(e => { console.error(e); reloadOrder(); });
      }
    } catch (e) { console.error(e); }
  };

  // Handle pack substitution popup choices (accept/keep/cancel)
  // choice = 'accept' → adjust pack header price by priceDiff
  // choice = 'keep'   → keep original price, record substitution only
  // choice = 'cancel' → dismiss popup, do nothing
  const handlePackSubChoice = async (choice) => {
    if (!packSubPopup || !order) return;
    const { lineId, substitute, priceDiff, line } = packSubPopup;

    if (choice === 'cancel') {
      setPackSubPopup(null);
      return;
    }

    // ---- Optimistic: update UI instantly, API in background ----
    const packHeaderLineId = line.PackHeaderLineId;
    setOrderLines(prev => prev.map(l => {
      if (l.LineId === lineId) {
        return {
          ...l,
          SubstitutionCode: substitute.SKU,
          SubstituteDescription: substitute.ProductName,
          IsSubstituted: true,
          OriginalProductId: l.OriginalProductId || l.ProductId,
          Product: substitute,
        };
      }
      if (choice === 'accept' && l.LineId === packHeaderLineId) {
        return { ...l, UnitPrice: Math.max(0, l.UnitPrice + priceDiff) };
      }
      return l;
    }));

    setPackSubPopup(null);
    setSubFinderLineId(null);
    setSubResults(null);
    setStatusMsg({
      type: 'success',
      text: choice === 'accept'
        ? `Substitute applied - pack price adjusted by ${priceDiff > 0 ? '+' : ''}£${priceDiff.toFixed(2)}`
        : `Substitute applied - pack price unchanged`,
    });

    // Fire API calls in background
    try {
      await updateOrderLine(order.OrderId, lineId, {
        ProductId: substitute.ProductId,
        SubstitutionReasonId: parseInt(selectedReasonId),
        SubstitutionPriceAccepted: choice === 'accept' ? 1 : 0,
      });
      if (choice === 'accept') {
        const headerLine = orderLines.find(l => l.LineId === packHeaderLineId);
        if (headerLine) {
          await updateOrderLine(order.OrderId, packHeaderLineId, {
            UnitPrice: Math.max(0, headerLine.UnitPrice + priceDiff),
          });
        }
      }
      reloadOrder(); // sync silently
    } catch (e) { console.error(e); reloadOrder(); }
  };

  // =====================================================
  // 9. Submit order
  // =====================================================
  // Calls POST /api/orders/:id/submit which pushes lines to HubSpot deal if linked.
  // In store mode (popup): shows confirmation dialog with "don't show again" option,
  // then attempts window.close() after a short delay.
  const handleSubmitOrder = async () => {
    if (!order) return;
    if (!canStartSubmit(submitState)) return;
    setSubmitState(SUBMIT_STATES.VALIDATING);
    setSubmitResult(null);
    if (orderLines.length === 0) {
      setSubmitState(SUBMIT_STATES.FAILED);
      setStatusMsg({ type: 'error', text: 'Add at least one line before submitting' });
      return;
    }
    if (!window.confirm('Submit this order? This will finalise the order and push to HubSpot if linked.')) {
      setSubmitState(SUBMIT_STATES.IDLE);
      return;
    }
    setSubmitting(true);
    setSubmitState(SUBMIT_STATES.SUBMITTING);
    setStatusMsg({ type: 'info', text: 'Submitting order to HubSpot. Please do not close this window.' });
    try {
      const submitRes = await hubspotSubmitOrder(order.OrderId);
      setSubmitResult(submitRes.data);
      await reloadOrder();
      setSubmitState(SUBMIT_STATES.SUCCESS);
      setStatusMsg({ type: 'success', text: buildSubmitSuccessMessage(submitRes.data) });

      // In store mode (opened via URL/popup), offer to close the popup window
      if (isStoreMode) {
        const skipMsg = localStorage.getItem('hideSubmitConfirm') === '1';
        if (skipMsg) {
          // User previously chose "don't show again" - close immediately
          setTimeout(() => {
            try { window.close(); } catch (_) {}
            setStatusMsg({ type: 'success', text: 'Order submitted! You can close this window.' });
          }, 1000);
        } else {
          setShowSubmitConfirm(true);
        }
      }
    } catch (e) {
      setSubmitState(SUBMIT_STATES.FAILED);
      setSubmitResult(e.response?.data || null);
      setStatusMsg({ type: 'error', text: buildSubmitFailureMessage(e) });
    }
    setSubmitting(false);
  };

  // =====================================================
  // 10. Filter toggle helpers
  // =====================================================
  // Multi-select toggle for free-from and dietary badge filters.
  // positionPopup: positions the floating allergen hover popup relative to the trigger element.
  // togglePackExpanded: toggles pack expansion in the CATALOG drawer (not the order table).
  // toggleSubAllergen: toggles allergen avoidance in the substitute search criteria.
  const toggleFreeFrom = (tag) => setSelectedFreeFrom(prev =>
    prev.includes(tag) ? prev.filter(x => x !== tag) : [...prev, tag]
  );
  const toggleDietary = (tag) => setSelectedDietary(prev =>
    prev.includes(tag) ? prev.filter(x => x !== tag) : [...prev, tag]
  );
  // Positions a floating tooltip popup above its trigger element using getBoundingClientRect
  const positionPopup = (e) => {
    const popup = e.currentTarget.querySelector('.tag-hover-popup');
    if (!popup) return;
    const r = e.currentTarget.getBoundingClientRect();
    popup.style.left = (r.left + r.width / 2) + 'px';
    popup.style.bottom = (window.innerHeight - r.top + 6) + 'px';
    popup.style.transform = 'translateX(-50%)';
  };

  // Toggle pack children expansion in the catalog drawer (separate from order table expansion)
  const togglePackExpanded = (packId) => {
    setExpandedPacks(prev => {
      const next = new Set(prev);
      if (next.has(packId)) next.delete(packId);
      else next.add(packId);
      return next;
    });
  };
  const toggleSubAllergen = (a) => setSubCriteria(prev => ({
    ...prev,
    avoidAllergens: prev.avoidAllergens.includes(a)
      ? prev.avoidAllergens.filter(x => x !== a)
      : [...prev.avoidAllergens, a]
  }));

  // ---- Derived values ----
  // Current user's site object (for displaying SiteCode badge in header)
  const userSite = currentUser ? sites.find(s => s.SiteId === currentUser.SiteId) : null;
  // Total order amount (inc. VAT) - pack headers + standalone lines.
  // Pack children are excluded; their price is covered by PackPrice on the header.
  const topLevelLines = orderLines.filter(l => !l.PackHeaderLineId);
  const totalAmount = orderTotals(topLevelLines).gross;

  // discountedTotal: discount applies at header level only (pack header + standalone).
  // Each line's discount £ is rounded to 2dp before being subtracted (matches SAP).
  const discountedTotal = orderTotals(topLevelLines).net;
  const hasDiscount = discountedTotal < totalAmount;
  // True if any product line in the order is marked as a promo pack - triggers free delivery notice
  const promoPackLines = orderLines.filter(l => l.PackId && !l.PackHeaderLineId && l.PromoPack);
  const hasPromoItem = promoPackLines.length > 0;

  // Sort lines so pack children always appear directly after their pack header.
  // Algorithm: iterate lines by LineNumber, for each pack header insert it + its children
  // as a group. Standalone lines are inserted as-is. Orphaned children go at the end.
  const sortedLines = React.useMemo(() => {
    const childrenMap = {};
    orderLines.forEach(l => {
      if (l.PackHeaderLineId) {
        if (!childrenMap[l.PackHeaderLineId]) childrenMap[l.PackHeaderLineId] = [];
        childrenMap[l.PackHeaderLineId].push(l);
      }
    });
    const usedIds = new Set();
    const result = [];
    const allByLineNumber = [...orderLines].sort((a, b) => a.LineNumber - b.LineNumber);
    for (const line of allByLineNumber) {
      if (usedIds.has(line.LineId)) continue;
      const isHeader = line.PackId && !line.ParentPackCode && !line.ProductId;
      if (isHeader) {
        result.push(line); usedIds.add(line.LineId);
        const children = (childrenMap[line.LineId] || []).sort((a, b) => a.LineNumber - b.LineNumber);
        for (const c of children) { result.push(c); usedIds.add(c.LineId); }
      } else if (!line.PackHeaderLineId) {
        result.push(line); usedIds.add(line.LineId);
      }
    }
    // Any remaining orphaned children (should not normally occur, but handles edge cases)
    for (const line of allByLineNumber) {
      if (!usedIds.has(line.LineId)) { result.push(line); usedIds.add(line.LineId); }
    }
    return result;
  }, [orderLines]);

  // Lines that have a ProductNote — drives the warnings panel and ⚠️ icons
  const warningLines = React.useMemo(() =>
    sortedLines
      .map((l, idx) => ({ ...l, _displayIdx: idx + 1 }))
      .filter(l => {
        const isPackHeader = !!(l.PackId && !l.ParentPackCode && !l.ProductId);
        const notes = isPackHeader ? l.Pack?.ProductNotes : l.Product?.ProductNotes;
        return notes && notes.trim().length > 0;
      }),
  [sortedLines]);

  // =====================================================
  // DISCOUNT HANDLERS
  // =====================================================
  // handleDiscountPctChange: user types in Discount % → recalculates £ value, debounces save.
  // handleDiscountValChange: user types in Discount £ → back-calculates %, debounces save.
  // After saving, we update order+lines state without resetting the discount inputs so the
  // user's in-progress typing is not interrupted.
  const handleDiscountPctChange = (newPct) => {
    const pctNum = Math.max(0, Math.min(100, round2(parseFloat(newPct) || 0)));
    setDiscountPct(newPct);
    setDiscountVal(orderTotals(topLevelLines.map(l => ({ ...l, DiscountPct: pctNum }))).discount.toFixed(2));
    // Optimistically apply the new % to all lines so discountedTotal and per-line
    // price cells update immediately - no wait for the debounced server save.
    setOrderLines(prev => prev.map(l => ({ ...l, DiscountPct: pctNum })));
    clearTimeout(discountTimerRef.current);
    discountTimerRef.current = setTimeout(async () => {
      if (!order) return;
      try {
        await applyOrderDiscount(order.OrderId, pctNum);
        const res = await getOrder(order.OrderId);
        setOrder(res.data);
        setOrderLines(res.data.Lines || []);
      } catch (e) {
        setStatusMsg({ type: 'error', text: 'Failed to apply discount' });
      }
    }, 600);
  };

  const handleDiscountValChange = (newVal) => {
    setDiscountVal(newVal);
    const valNum = parseFloat(newVal) || 0;
    // Stored as DECIMAL(5,2), so round here to keep the optimistic UI equal to what is saved.
    const pctNum = totalAmount > 0 ? Math.min(100, round2((valNum / totalAmount) * 100)) : 0;
    setDiscountPct(pctNum.toFixed(2));
    // Optimistically apply the back-calculated % to all lines immediately.
    setOrderLines(prev => prev.map(l => ({ ...l, DiscountPct: pctNum })));
    clearTimeout(discountTimerRef.current);
    discountTimerRef.current = setTimeout(async () => {
      if (!order) return;
      try {
        await applyOrderDiscount(order.OrderId, pctNum);
        const res = await getOrder(order.OrderId);
        setOrder(res.data);
        setOrderLines(res.data.Lines || []);
      } catch (e) {
        setStatusMsg({ type: 'error', text: 'Failed to apply discount' });
      }
    }, 600);
  };

  // handleLineDiscountChange: user edits the Disc. % cell on an order line.
  // Discount is applied at the line level only - pack children do not receive it.
  // Optimistically updates local state, then debounces the API save (600 ms).
  const handleLineDiscountChange = (lineId, newPct) => {
    const pct = Math.max(0, Math.min(100, parseFloat(newPct) || 0));
    setOrderLines(prev => prev.map(l => l.LineId === lineId ? { ...l, DiscountPct: pct } : l));
    clearTimeout(lineDiscountTimers.current[lineId]);
    lineDiscountTimers.current[lineId] = setTimeout(async () => {
      if (!order) return;
      try {
        await updateOrderLine(order.OrderId, lineId, { DiscountPct: pct });
      } catch (e) {
        setStatusMsg({ type: 'error', text: 'Failed to update line discount' });
        reloadOrder();
      }
    }, 600);
  };

  // =====================================================
  // RENDER
  // =====================================================
  // Loading state - shown during boot (reference data + order creation/load)
  if (loading) {
    return (
      <>
        <div className="top-header"><h1>Order Lines Editor</h1></div>
        <div className="page-content">
          <div className="hs-card">
            <div className="hs-card-body" style={{ textAlign: 'center', padding: 60 }}>
              <div className="loading-spinner"></div>
              <p className="text-muted" style={{ marginTop: 16 }}>Setting up your order…</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Main render:
  // ┌─ HEADER (order number + HubSpot badge + site badge) ─────────────────────────────┐
  // ├─ STATUS MESSAGE (success/error/info toast, dismissable) ─────────────────────────┤
  // ├─ MAIN LAYOUT ────────────────────────────────────────────────────────────────────┤
  // │   Left: Order summary bar + Order lines table (shifts right when drawer is open) │
  // │   Right: Product picker drawer (slides in, fixed 50% width)                     │
  // │     Tab: Catalog - search/filter, product list, pack expansion                  │
  // │     Tab: Substitute - recommended (Sub1/Sub2), algorithm search, browse         │
  // ├─ PACK SUBSTITUTION PRICE POPUP (modal overlay) ──────────────────────────────────┤
  // └─ SUBMIT CONFIRMATION DIALOG (store mode only) ───────────────────────────────────┘
  return (
    <>
      {/* ---- HEADER: order title + inline order number + HubSpot deal/order badges ---- */}
      <div className="top-header">
        <h1>
          Order Lines Editor
          {order && <span style={{ fontWeight: 400, fontSize: 16, marginLeft: 12, color: '#64748b' }}>
            {order.OrderNumber}
            {order.HubSpotDealId && <span className="hs-badge hs-badge-pack" style={{ marginLeft: 8, fontSize: 11 }}>HubSpot</span>}
            {order.HubSpotOrderId && <span className="hs-badge" style={{ marginLeft: 6, fontSize: 10, background: '#2563eb', color: '#fff' }}>HS-{order.HubSpotOrderId}</span>}
          </span>}
        </h1>
        <div className="top-header-actions" style={{ gap: 12, display: 'flex', alignItems: 'center' }}>
          {userSite && <span className="hs-badge hs-badge-available">{userSite.SiteCode}</span>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img
              src="/oakhouse-logo.png"
              alt="Oakhouse Foods"
              style={{ width: 32, height: 32, objectFit: 'contain' }}
              onError={e => { e.target.style.display = 'none'; }}
            />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#2e7d32', fontFamily: 'Georgia, serif' }}>Oakhouse Foods</span>
          </div>
        </div>
      </div>

      <div className="page-content">
        {/* ---- MISSING FRANCHISE ID WARNING ---- */}
        {isStoreMode && !urlStoreId && (
          <div className="hs-card mb-md" style={{ background: '#fffbeb', borderLeft: '4px solid #d97706' }}>
            <div className="hs-card-body" style={{ padding: '12px 16px' }}>
              <strong style={{ fontSize: 14, color: '#92400e' }}>⚠ Franchise ID is not available.</strong>
              <span style={{ fontSize: 14, color: '#92400e' }}> Stock quantities cannot be determined - all items are shown as 0.
                Please close this window, go back to HubSpot, and make sure the Franchise ID is set on the order before reopening.
              </span>
            </div>
          </div>
        )}

        {/* ---- STATUS MESSAGE ---- */}
        {statusMsg && (
          <div className={`hs-card mb-md`} style={{
            background: statusMsg.type === 'error' ? '#fef2f2' : statusMsg.type === 'success' ? '#f0fdf4' : '#eff6ff',
            borderLeft: `4px solid ${statusMsg.type === 'error' ? '#dc2626' : statusMsg.type === 'success' ? '#16a34a' : '#2563eb'}`,
          }}>
            <div className="hs-card-body" style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14 }}>
                {statusMsg.type === 'error' ? '⚠ ' : statusMsg.type === 'success' ? '✓ ' : 'ℹ '}
                {statusMsg.text}
              </span>
              <button className="hs-btn hs-btn-sm hs-btn-secondary" onClick={() => setStatusMsg(null)}>×</button>
            </div>
          </div>
        )}

        {/* ---- MAIN LAYOUT: left panel shifts right when drawer opens ---- */}
        <div>

          {/* ======== ORDER LINES PANEL (left side) ========
             marginRight animates to 44% when the product picker drawer is open */}
          <div style={{ transition: 'margin-right .25s ease', marginRight: showProductPicker ? '44%' : 0 }}>
            {/* ---- ORDER SUMMARY BAR ---- */}
            {/* Shows order number (click to edit inline), site, status, line count, total.
                Also shows substitution codes summary and the main action buttons.
                TO ADD A NEW SUMMARY FIELD: add a <div> inside the flex container below. */}
            <div className="hs-card mb-md" style={{
              background: 'linear-gradient(135deg, #fdf4f0 0%, #fff7f5 100%)',
              position: 'sticky', top: 56, zIndex: 40,
              borderBottom: '2px solid #fed7aa',
            }}>
              <div className="hs-card-body" style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div>
                    <span className="text-muted text-sm">Order</span><br />
                    {editingOrderNo ? (
                      <input
                        className="hs-input"
                        style={{ width: 150, padding: '2px 6px', fontSize: 14, fontWeight: 700 }}
                        autoFocus
                        value={orderNoDraft}
                        onChange={e => setOrderNoDraft(e.target.value)}
                        onKeyDown={async e => {
                          if (e.key === 'Enter') {
                            if (orderNoDraft.trim() && order) {
                              try {
                                await updateOrder(order.OrderId, { OrderNumber: orderNoDraft.trim() });
                                await reloadOrder();
                                setStatusMsg({ type: 'success', text: `Order number updated to ${orderNoDraft.trim()}` });
                              } catch (err) {
                                setStatusMsg({ type: 'error', text: 'Failed to update order number' });
                              }
                            }
                            setEditingOrderNo(false);
                          } else if (e.key === 'Escape') {
                            setEditingOrderNo(false);
                          }
                        }}
                        onBlur={async () => {
                          if (orderNoDraft.trim() && order && orderNoDraft.trim() !== order.OrderNumber) {
                            try {
                              await updateOrder(order.OrderId, { OrderNumber: orderNoDraft.trim() });
                              await reloadOrder();
                              setStatusMsg({ type: 'success', text: `Order number updated to ${orderNoDraft.trim()}` });
                            } catch (err) {
                              setStatusMsg({ type: 'error', text: 'Failed to update order number' });
                            }
                          }
                          setEditingOrderNo(false);
                        }}
                      />
                    ) : (
                      <strong
                        style={{ cursor: 'pointer', borderBottom: '1px dashed #94a3b8' }}
                        title="Click to edit order number"
                        onClick={() => { setOrderNoDraft(order?.OrderNumber || ''); setEditingOrderNo(true); }}
                      >{order?.OrderNumber || '-'}</strong>
                    )}
                  </div>
                  <div>
                    <span className="text-muted text-sm">Site</span><br />
                    <strong>{order?.Site?.SiteName || order?.Site?.SiteCode || '-'}</strong>
                  </div>
                  <div>
                    <span className="text-muted text-sm">Site ID</span><br />
                    <strong>{order?.Site?.SiteId || '-'}</strong>
                  </div>
                  {order?.WarehouseId && (
                    <div>
                      <span className="text-muted text-sm">Warehouse</span><br />
                      <strong className="font-mono" style={{ fontSize: 13 }}>{order.WarehouseId}</strong>
                    </div>
                  )}
                  <div>
                    <span className="text-muted text-sm">Status</span><br />
                    <span className={`hs-badge ${order?.Status === 'Submitted' ? 'hs-badge-available' : 'hs-badge-draft'}`}>{order?.Status}</span>
                  </div>
                  <div>
                    <span className="text-muted text-sm">Lines</span><br />
                    <strong>{orderLines.filter(l => !l.PackHeaderLineId).length}</strong>
                  </div>
                  <div>
                    <span className="text-muted text-sm">Items</span><br />
                    <strong>{orderLines.filter(l => l.ProductId).reduce((sum, l) => sum + (l.Quantity || 0), 0)}</strong>
                  </div>
                  <div>
                    <span className="text-muted text-sm">Total (inc. VAT)</span><br />
                    {hasDiscount ? (
                      <>
                        <span style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: 13 }}>£{totalAmount.toFixed(2)}</span>{' '}
                        <strong style={{ fontSize: 18, color: 'var(--hs-orange)' }}>£{discountedTotal.toFixed(2)}</strong>
                      </>
                    ) : (
                      <strong style={{ fontSize: 18, color: 'var(--hs-orange)' }}>£{totalAmount.toFixed(2)}</strong>
                    )}
                  </div>
                  <div>
                    <span className="text-muted text-sm">Discount %</span><br />
                    <input
                      type="number" min="0" max="100" step="0.01"
                      className="hs-input"
                      style={{ width: 70, padding: '2px 6px', fontSize: 14, textAlign: 'right' }}
                      value={discountPct}
                      onChange={e => handleDiscountPctChange(e.target.value)}
                    />
                  </div>
                  <div>
                    <span className="text-muted text-sm">Discount £</span><br />
                    <input
                      type="number" min="0" step="0.01"
                      className="hs-input"
                      style={{ width: 80, padding: '2px 6px', fontSize: 14, textAlign: 'right' }}
                      value={discountVal}
                      onChange={e => handleDiscountValChange(e.target.value)}
                    />
                  </div>
                  {(() => {
                    const subs = orderLines
                      .filter(l => l.IsSubstituted && l.OriginalProduct && l.PackHeaderLineId)
                      .map(l => `${l.OriginalProduct.SKU}-${l.SubstitutionCode || '?'}-${l.SubstitutionPriceAccepted ? 1 : 0}`);
                    return subs.length > 0 ? (
                      <div>
                        <span className="text-muted text-sm">Sub.</span><br />
                        <strong style={{ fontSize: 13 }}>({subs.join(', ')})</strong>
                      </div>
                    ) : null;
                  })()}
                  {hasPromoItem && (
                    <div style={{
                      background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6,
                      padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      <span style={{ fontSize: 16 }}>🎁</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#15803d' }}>Promo Pack - Free delivery on this order</div>
                        <div style={{ fontSize: 11, color: '#16a34a' }}>
                          {promoPackLines.map(l => l.Pack?.PackName || l.Pack?.PackSKU || 'Pack').join(', ')}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="hs-btn hs-btn-primary hs-btn-sm" onClick={() => { if (showProductPicker) resetCatalogFilters(); setShowProductPicker(!showProductPicker); }}>
                    {showProductPicker ? '✕ Close Catalog' : '+ Add Products'}
                  </button>
                  <button className="hs-btn hs-btn-sm" style={{ background: order?.Status === 'Submitted' ? '#2563eb' : '#16a34a', color: '#fff' }}
                    onClick={handleSubmitOrder} disabled={submitting || hubspotSyncing || isSubmitInFlight(submitState)}>
                    {getSubmitButtonLabel(submitState, order?.Status)}
                  </button>
                  {submitResult?.correlationId && submitState !== SUBMIT_STATES.SUCCESS && (
                    <span style={{ fontSize: 11, color: '#64748b', alignSelf: 'center' }}>
                      Ref {submitResult.correlationId}
                    </span>
                  )}
                  {order?.SubmitCount > 0 && (
                    <span style={{ fontSize: 11, color: '#64748b', alignSelf: 'center' }}
                      title={order.LastSubmittedBy ? `Last by ${order.LastSubmittedBy} at ${new Date(order.LastSubmittedAt).toLocaleString()}` : ''}>
                      Submitted {order.SubmitCount}×
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* ---- PRODUCT NOTES WARNINGS ---- */}
            {warningLines.length > 0 && (
              <div className="hs-card mb-md" style={{ border: '2px solid #dc2626', borderRadius: 6 }}>
                <div className="hs-card-header" style={{ borderBottom: '1px solid #fecaca', padding: '8px 16px', background: '#fff1f2' }}>
                  <h3 style={{ color: '#dc2626', margin: 0, fontSize: 13 }}>⚠️ Product Notes ({warningLines.length} line{warningLines.length !== 1 ? 's' : ''})</h3>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="hs-table" style={{ minWidth: 500 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 70 }}>Line No</th>
                        <th style={{ width: 120 }}>SKU</th>
                        <th>Product Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {warningLines.map(l => {
                        const isHeader = !!(l.PackId && !l.ParentPackCode && !l.ProductId);
                        const sku = isHeader ? l.Pack?.PackSKU : l.Product?.SKU;
                        const notes = isHeader ? l.Pack?.ProductNotes : l.Product?.ProductNotes;
                        return (
                          <tr key={l.LineId}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              if (l.PackHeaderLineId) {
                                setExpandedOrderPacks(prev => { const n = new Set(prev); n.add(l.PackHeaderLineId); return n; });
                              }
                              setHighlightedLineId(l.LineId);
                              setTimeout(() => {
                                const el = document.getElementById(`order-line-${l.LineId}`);
                                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }, 50);
                              setTimeout(() => setHighlightedLineId(null), 2500);
                            }}
                          >
                            <td className="font-mono">{l._displayIdx}</td>
                            <td className="font-mono text-sm" style={{ fontWeight: 600 }}>{sku || '-'}</td>
                            <td style={{ fontSize: 13, color: '#92400e' }}>{notes}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ---- ORDER LINES TABLE ---- */}
            {/* Renders sortedLines - pack headers + their children always grouped together.
                Each row conditionally renders as: PACK HEADER | PACK CHILD | STANDALONE.
                Pack children are hidden when their parent pack is collapsed (expandedOrderPacks).
                TO ADD A NEW COLUMN: add <th> in thead + <td> in both row type blocks below. */}
            <div className="hs-card">
              <div className="hs-card-header flex-between">
                <h2>Order Lines</h2>
              </div>

              {orderLines.length === 0 ? (
                <div className="hs-card-body">
                  <div className="empty-state" style={{ padding: 40 }}>
                    <div className="empty-icon">📋</div>
                    <h3>No order lines yet</h3>
                    <p>Open the product catalog to search, filter, and add products to this order</p>
                    <button className="hs-btn hs-btn-primary" onClick={() => setShowProductPicker(true)}>
                      Open Product Catalog
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ maxHeight: 'calc(100vh - 400px)', overflow: 'auto' }}>
                  <div style={{ overflowX: 'auto' }}>
              <table className="hs-table" style={{ minWidth: 900 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 30 }}>#</th>
                        <th>Product</th>
                        <th style={{ width: 80 }}>Pack Code</th>
                        <th style={{ width: 90 }}>Sub. Code</th>
                        <th>Allergens</th>
                        <th style={{ width: 40, textAlign: 'center' }}>Notes</th>
                        <th style={{ width: 60 }}>Stock</th>
                        <th style={{ width: 70 }}>Qty</th>
                        <th className="text-right" style={{ width: 75 }}>Ex-VAT</th>
                        <th className="text-right" style={{ width: 65 }}>VAT</th>
                        <th style={{ width: 50, textAlign: 'center' }}>VAT %</th>
                        <th style={{ width: 60 }}>Disc. %</th>
                        <th className="text-right" style={{ width: 80 }}>Line Total</th>
                        <th style={{ width: 90 }}>Next Avail.</th>
                        <th style={{ width: 110 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedLines.map((line, idx) => {
                        const prod = line.Product;
                        const isSubOpen = subFinderLineId === line.LineId;
                        const isPackChild = !!line.ParentPackCode;
                        const isPackHeader = !!(line.PackId && !line.ParentPackCode && !line.ProductId);
                        // Look up next availability from catalog products
                        const catalogProd = products.find(p => p.ProductId === line.ProductId);
                        const lineNextAvail = catalogProd?.NextAvailability || null;
                        const packInfo = isPackHeader ? line.Pack : null;

                        // Count children for this pack header
                        const packChildCount = isPackHeader
                          ? sortedLines.filter(l => l.PackHeaderLineId === line.LineId).length
                          : 0;

                        return (
                          <React.Fragment key={line.LineId}>
                            {/* ===== PACK HEADER ROW ===== */}
                            {isPackHeader ? (
                              <tr id={`order-line-${line.LineId}`} style={{
                                background: highlightedLineId === line.LineId ? '#fef9c3' : '#fff8f1',
                                borderLeft: `3px solid ${highlightedLineId === line.LineId ? '#f59e0b' : 'var(--hs-orange)'}`,
                                cursor: 'pointer',
                                ...(isSubOpen ? { background: '#fff7ed' } : {}),
                              }}
                              onClick={() => setExpandedOrderPacks(prev => {
                                const next = new Set(prev);
                                next.has(line.LineId) ? next.delete(line.LineId) : next.add(line.LineId);
                                return next;
                              })}>
                                <td className="text-muted" style={{ fontWeight: 600 }}>
                                  <span style={{ fontSize: 12, color: '#64748b', userSelect: 'none', marginRight: 4 }}>
                                    {expandedOrderPacks.has(line.LineId) ? '▼' : '▶'}
                                  </span>
                                  {idx + 1}
                                </td>
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 18 }}>📦</span>
                                    <div>
                                      <strong style={{ fontSize: 14 }}>{packInfo?.PackName || 'Pack'}</strong>
                                      {line.PromoPack && (
                                        <span className="hs-badge" style={{ background: '#15803d', color: '#fff', fontSize: 9, marginLeft: 6, verticalAlign: 'middle' }}>🎁 Promo</span>
                                      )}
                                      <div className="text-muted text-sm font-mono">{packInfo?.PackSKU || '-'}</div>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span className="hs-badge hs-badge-pack" style={{ fontSize: 10 }}>{packInfo?.PackSKU || '-'}</span>
                                </td>
                                <td><span className="text-muted text-sm">-</span></td>
                                <td>
                                  <span className="text-muted text-sm">{packChildCount} items</span>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  {line.Pack?.ProductNotes ? (
                                    <span className="tag-hover-trigger" onMouseEnter={positionPopup}>
                                      <span style={{ fontSize: 15, cursor: 'help' }}>⚠️</span>
                                      <span className="tag-hover-popup" style={{ maxWidth: 280, whiteSpace: 'normal' }}>
                                        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Product Note</div>
                                        <div style={{ fontSize: 11 }}>{line.Pack.ProductNotes}</div>
                                      </span>
                                    </span>
                                  ) : null}
                                </td>
                                <td>
                                  {(() => {
                                    // Pack stock = min available stock across all child lines
                                    const children = sortedLines.filter(l => l.PackHeaderLineId === line.LineId);
                                    const childStocks = children.map(c => c.StockAtSite?.AvailableQty).filter(q => q != null);
                                    if (childStocks.length === 0) return <span className="text-muted text-sm">-</span>;
                                    const minStock = Math.min(...childStocks);
                                    return (
                                      <span className="font-mono" style={{
                                        fontWeight: 600, fontSize: 13,
                                        color: minStock <= 0 ? '#dc2626' : minStock < 10 ? '#d97706' : '#16a34a'
                                      }}>{minStock}</span>
                                    );
                                  })()}
                                </td>
                                <td>
                                  <input type="number" className="hs-input" min="1"
                                    max={(line.Product?.IsActive === false || line.Product?.EStop === true || line.Pack?.IsActive === false) ? line.Quantity : undefined}
                                    style={{ width: 60, padding: '4px 6px', textAlign: 'center' }}
                                    value={line.Quantity}
                                    onClick={e => e.stopPropagation()}
                                    onChange={e => handleQtyChange(line.LineId, parseInt(e.target.value) || 1)} />
                                </td>
                                <td className="text-right font-mono" style={{ fontSize: 12 }}>
                                  £{calcExVat(line.UnitPrice, line.TaxCode).toFixed(2)}
                                </td>
                                <td className="text-right font-mono" style={{ fontSize: 12 }}>
                                  £{(parseFloat(line.UnitPrice || 0) - calcExVat(line.UnitPrice, line.TaxCode)).toFixed(2)}
                                </td>
                                <td style={{ textAlign: 'center', fontSize: 12 }}>
                                  {line.TaxCode === 'A0' ? '0%' : line.TaxCode === 'A1' ? '20%' : '-'}
                                </td>
                                <td>
                                  <input type="number" min="0" max="100" step="0.01"
                                    className="hs-input"
                                    style={{ width: 55, padding: '4px 4px', textAlign: 'right', fontSize: 12 }}
                                    value={parseFloat(line.DiscountPct) || 0}
                                    onClick={e => e.stopPropagation()}
                                    onChange={e => handleLineDiscountChange(line.LineId, e.target.value)} />
                                </td>
                                <td className="text-right font-mono" style={{ fontWeight: 700, color: 'var(--hs-orange)' }}>
                                  £{lineAmounts(line.Quantity, line.UnitPrice, line.DiscountPct).net.toFixed(2)}
                                </td>
                                <td><span className="text-muted text-sm">-</span></td>
                                <td>
                                  <button className="hs-btn hs-btn-sm hs-btn-danger"
                                    title="Remove entire pack"
                                    onClick={(e) => { e.stopPropagation(); handleRemoveLine(line.LineId); }}>
                                    ✕ Pack
                                  </button>
                                </td>
                              </tr>
                            ) : (
                              /* ===== PACK CHILD or STANDALONE ROW ===== */
                              isPackChild && !expandedOrderPacks.has(line.PackHeaderLineId) ? null : (
                              <tr id={`order-line-${line.LineId}`} style={{
                                ...(isSubOpen ? { background: '#fff7ed' } : {}),
                                ...(isPackChild ? { background: '#f8fafc' } : {}),
                                ...(highlightedLineId === line.LineId ? { background: '#fef9c3', outline: '2px solid #f59e0b' } : {}),
                              }}>
                                <td className="text-muted">
                                  {isPackChild ? (
                                    <span style={{ display: 'inline-block', width: 16, textAlign: 'right', marginRight: 2 }}>
                                      <span style={{ display: 'inline-block', width: 12, borderLeft: '2px solid var(--hs-orange)', borderBottom: '2px solid var(--hs-orange)', height: 10, verticalAlign: 'middle' }} />
                                    </span>
                                  ) : null}
                                  <span style={isPackChild ? { color: '#94a3b8', fontSize: 12 } : {}}>{idx + 1}</span>
                                </td>
                                <td>
                                  <div style={isPackChild ? { paddingLeft: 24 } : {}}>
                                    {editingProductLineId === line.LineId ? (
                                      <div style={{ position: 'relative' }}>
                                        <input
                                          autoFocus
                                          ref={productInputRef}
                                          className="hs-input"
                                          style={{ width: '100%', fontSize: 13, padding: '4px 8px' }}
                                          placeholder="Type to filter products…"
                                          value={productSearchText}
                                          onChange={e => setProductSearchText(e.target.value)}
                                          onFocus={() => {
                                            setTimeout(() => {
                                              if (productInputRef.current) {
                                                const rect = productInputRef.current.getBoundingClientRect();
                                                setPickerPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
                                              }
                                            }, 0);
                                          }}
                                          onBlur={() => setTimeout(() => { setEditingProductLineId(null); setProductSearchText(''); setPickerPos(null); }, 150)}
                                        />
                                      </div>
                                    ) : (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {prod?.ThumbnailUrl && (
                                          <span
                                            className="tag-hover-trigger"
                                            style={{ cursor: 'default', flexShrink: 0 }}
                                            onMouseEnter={e => {
                                              const popup = e.currentTarget.querySelector('.thumb-popup');
                                              if (!popup) return;
                                              const rect = e.currentTarget.getBoundingClientRect();
                                              if (window.innerHeight - rect.bottom < 320) {
                                                popup.style.top = 'auto'; popup.style.bottom = 'calc(100% + 4px)';
                                              } else {
                                                popup.style.top = 'calc(100% + 4px)'; popup.style.bottom = 'auto';
                                              }
                                            }}
                                          >
                                            <img src={prod.ThumbnailUrl} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 3, verticalAlign: 'middle', display: 'block' }} />
                                            {prod?.ImageUrl && (
                                              <span className="thumb-popup">
                                                <img src={prod.ImageUrl} alt="" style={{ width: 300, height: 'auto', display: 'block' }} />
                                              </span>
                                            )}
                                          </span>
                                        )}
                                        <span style={{ fontSize: 13, fontWeight: 500 }} title={prod?.Description || ''}>
                                          {prod ? prod.ProductName : '- No product -'}
                                        </span>
                                        {!isPackChild && (
                                          <button className="hs-btn hs-btn-sm hs-btn-secondary"
                                            style={{ fontSize: 10, padding: '1px 6px', lineHeight: 1.4 }}
                                            onClick={() => { setEditingProductLineId(line.LineId); setProductSearchText(''); }}
                                            title="Change product">
                                            ✏️
                                          </button>
                                        )}
                                      </div>
                                    )}
                                    {prod && (
                                      <div className="text-muted text-sm" style={{ marginTop: 2 }}>
                                        {prod.SKU} - {prod.Description?.substring(0, 60)}…
                                      </div>
                                    )}
                                    {line.IsSubstituted && (
                                      <div style={{ marginTop: 2 }}>
                                        <span className="hs-badge hs-badge-pending" style={{ fontSize: 10 }}>Substituted</span>
                                        {line.OriginalProduct && (
                                          <span className="text-muted text-sm" style={{ marginLeft: 4 }}>
                                            was: {line.OriginalProduct.ProductName}
                                          </span>
                                        )}
                                        {line.SubstituteDescription && (
                                          <span className="text-sm" style={{ marginLeft: 4, color: 'var(--hs-orange)', fontWeight: 600 }}>
                                            → {line.SubstituteDescription}
                                          </span>
                                        )}
                                      </div>
                                    )}

                                  </div>
                                </td>
                                <td>
                                  {isPackChild ? (
                                    <span className="hs-badge hs-badge-pack" style={{ fontSize: 10 }}>{line.ParentPackCode}</span>
                                  ) : (
                                    <span className="text-muted text-sm">-</span>
                                  )}
                                </td>
                                <td>
                                  {line.SubstitutionCode ? (
                                    <div>
                                      <span className="font-mono text-sm" style={{ fontWeight: 600, color: 'var(--hs-orange)' }}>{line.SubstitutionCode}</span>
                                      {line.SubstituteDescription && (
                                        <div className="text-muted text-sm" style={{ fontSize: 10 }}>{line.SubstituteDescription}</div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-muted text-sm">-</span>
                                  )}
                                </td>
                                <td>
                                  {prod && (prod.Allergens || []).map(a => (
                                    <span key={a} className="hs-badge" style={{
                                      background: ALLERGEN_COLOURS[a] || '#64748b', color: '#fff',
                                      marginRight: 2, fontSize: 9,
                                    }}>{a}</span>
                                  ))}
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  {prod?.ProductNotes ? (
                                    <span className="tag-hover-trigger" onMouseEnter={positionPopup}>
                                      <span style={{ fontSize: 15, cursor: 'help' }}>⚠️</span>
                                      <span className="tag-hover-popup" style={{ maxWidth: 280, whiteSpace: 'normal' }}>
                                        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Product Note</div>
                                        <div style={{ fontSize: 11 }}>{prod.ProductNotes}</div>
                                      </span>
                                    </span>
                                  ) : null}
                                </td>
                                <td>
                                  {line.StockAtSite ? (
                                    <span className="font-mono" style={{
                                      fontWeight: 600, fontSize: 13,
                                      color: line.StockAtSite.AvailableQty <= 0 ? '#dc2626' : line.StockAtSite.AvailableQty < 10 ? '#d97706' : '#16a34a'
                                    }}>{line.StockAtSite.AvailableQty}</span>
                                  ) : <span className="text-muted text-sm">-</span>}
                                </td>
                                <td>
                                  {isPackChild ? (
                                    <span className="font-mono" style={{ fontSize: 13 }}>{line.Quantity}</span>
                                  ) : (
                                    <input type="number" className="hs-input" min="1"
                                      max={(line.Product?.IsActive === false || line.Product?.EStop === true || line.Pack?.IsActive === false) ? line.Quantity : undefined}
                                      style={{ width: 60, padding: '4px 6px', textAlign: 'center' }}
                                      value={line.Quantity}
                                      onChange={e => handleQtyChange(line.LineId, parseInt(e.target.value) || 1)} />
                                  )}
                                </td>
                                <td className="text-right font-mono" style={{ fontSize: 12 }}>
                                  £{calcExVat(line.UnitPrice, line.TaxCode).toFixed(2)}
                                </td>
                                <td className="text-right font-mono" style={{ fontSize: 12, color: '#64748b' }}>
                                  £{(parseFloat(line.UnitPrice || 0) - calcExVat(line.UnitPrice, line.TaxCode)).toFixed(2)}
                                </td>
                                <td style={{ textAlign: 'center', fontSize: 12 }}>
                                  <span className="font-mono" style={{ color: '#475569' }}>
                                    {line.TaxCode === 'A0' ? '0%' : line.TaxCode === 'A1' ? '20%' : '-'}
                                  </span>
                                </td>
                                <td>
                                  {isPackChild ? (
                                    <span className="text-muted text-sm">-</span>
                                  ) : (
                                    <input type="number" min="0" max="100" step="0.01"
                                      className="hs-input"
                                      style={{ width: 55, padding: '4px 4px', textAlign: 'right', fontSize: 12 }}
                                      value={parseFloat(line.DiscountPct) || 0}
                                      onChange={e => handleLineDiscountChange(line.LineId, e.target.value)} />
                                  )}
                                </td>
                                <td className="text-right font-mono" style={{ fontWeight: 600 }}>
                                  <span>£{lineAmounts(line.Quantity, line.UnitPrice, isPackChild ? 0 : line.DiscountPct).net.toFixed(2)}</span>
                                </td>
                                <td>
                                  {lineNextAvail ? (
                                    <div style={{ lineHeight: 1.3 }}>
                                      <div className="text-sm font-mono" style={{ fontWeight: 600, color: '#2563eb' }}>{lineNextAvail.ExpectedDate}</div>
                                      <div className="text-sm text-muted">+{lineNextAvail.ExpectedQty} units</div>
                                    </div>
                                  ) : <span className="text-muted text-sm">-</span>}
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: 4 }}>
                                    <button className="hs-btn hs-btn-sm hs-btn-secondary"
                                      title="Find substitute"
                                      onClick={() => openSubFinder(line)}
                                      disabled={!line.ProductId}>
                                      🔄
                                    </button>
                                    <button className="hs-btn hs-btn-sm hs-btn-danger"
                                      title="Remove line"
                                      onClick={() => handleRemoveLine(line.LineId)}>
                                      ✕
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}

                          </React.Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid #e2e8f0' }}>
                        <td colSpan={11} className="text-right" style={{ fontWeight: 600, padding: '12px 16px' }}>
                          {hasDiscount ? 'Order Total inc. VAT (after discount)' : 'Order Total (inc. VAT)'}
                        </td>
                        <td className="text-right font-mono" style={{ fontWeight: 700, fontSize: 16, color: 'var(--hs-orange)' }}>
                          {hasDiscount ? (
                            <>
                              <span style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: 12, display: 'block' }}>£{totalAmount.toFixed(2)}</span>
                              £{discountedTotal.toFixed(2)}
                            </>
                          ) : (
                            `£${totalAmount.toFixed(2)}`
                          )}
                        </td>
                        <td></td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                  </div>
                </div>
              )}
            </div>

            {/* ======== DEBUG: HubSpot Payload Preview ======== */}
            {/* ⚠ TEMPORARY - remove once HubSpot sync is verified */}
            {order && (
              <div className="hs-card" style={{ marginTop: 12, borderLeft: '4px solid #d97706' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => { setShowHsDebug(v => { const next = !v; if (next && !hsPreview) loadHsPreview(); return next; }); }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#92400e' }}>
                    🔬 HubSpot Payload Preview
                    <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8', marginLeft: 10 }}>debug only - remove before go-live</span>
                  </span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {showHsDebug && (
                      <button className="hs-btn hs-btn-sm hs-btn-secondary" style={{ fontSize: 11 }}
                        onClick={e => { e.stopPropagation(); loadHsPreview(); }}>
                        🔄 Refresh
                      </button>
                    )}
                    <span style={{ fontSize: 12, color: '#64748b' }}>{showHsDebug ? '▲' : '▼'}</span>
                  </div>
                </div>

                {showHsDebug && (
                  <div style={{ padding: '0 14px 14px' }}>
                    {hsPreviewLoading && (
                      <div style={{ color: '#64748b', fontSize: 13, padding: '12px 0' }}>Loading…</div>
                    )}
                    {!hsPreviewLoading && hsPreview?.error && (
                      <div style={{ color: '#dc2626', fontSize: 13, padding: '8px 0' }}>⚠ {hsPreview.error}</div>
                    )}
                    {!hsPreviewLoading && hsPreview && !hsPreview.error && (
                      <>
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
                            Deal Properties - deal {hsPreview.dealId || '(no deal linked)'}
                          </div>
                          <div style={{ background: '#0f172a', borderRadius: 6, padding: '10px 14px' }}>
                            {Object.entries(hsPreview.dealProperties || {}).map(([k, v]) => (
                              <div key={k} style={{ display: 'flex', gap: 12, marginBottom: 4, fontFamily: 'monospace', fontSize: 12 }}>
                                <span style={{ color: '#7dd3fc', minWidth: 230 }}>{k}</span>
                                <span style={{ color: '#86efac' }}>{JSON.stringify(v)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
                            Line Items ({hsPreview.lineCount}) - HubSpot API properties
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {(hsPreview.lineItems || []).map((item, i) => (
                              <div key={i} style={{ background: '#0f172a', borderRadius: 6, padding: '10px 14px' }}>
                                <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#f472b6', fontWeight: 600, marginBottom: 6 }}>
                                  [{i + 1}] {item.name}
                                </div>
                                {Object.entries(item).filter(([k]) => k !== 'name').map(([k, v]) => (
                                  <div key={k} style={{ display: 'flex', gap: 12, marginBottom: 3, fontFamily: 'monospace', fontSize: 12 }}>
                                    <span style={{ color: '#7dd3fc', minWidth: 180 }}>{k}</span>
                                    <span style={{ color: '#86efac' }}>{JSON.stringify(v)}</span>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

          </div>

          {/* ---- Pack substitution price-diff popup (modal overlay) ---- */}
          {/* Appears when substituting a pack child line - user must decide whether
              to accept the price difference or keep the original pack price.
              Reason selection is mandatory before confirming. */}
          {packSubPopup && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,.4)', zIndex: 10000,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{
                background: '#fff', borderRadius: 12, padding: 24, width: 440,
                boxShadow: '0 8px 32px rgba(0,0,0,.2)',
              }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>
                  {Math.abs(packSubPopup.priceDiff) > 0.005 ? '⚠️ Pack Substitution - Price Difference' : '🔄 Pack Substitution'}
                </h3>
                <p style={{ margin: '0 0 8px', fontSize: 14, color: '#475569' }}>
                  Substituting <strong>{packSubPopup.line?.Product?.ProductName || 'original'}</strong> with{' '}
                  <strong>{packSubPopup.substitute.ProductName}</strong> in this pack.
                </p>

                {Math.abs(packSubPopup.priceDiff) > 0.005 && (
                  <>
                    <div style={{
                      background: '#f8fafc', borderRadius: 8, padding: 12, margin: '12px 0',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <div>
                        <div className="text-muted text-sm">Original item price</div>
                        <strong className="font-mono">£{packSubPopup.originalPrice.toFixed(2)}</strong>
                      </div>
                      <div style={{ fontSize: 20, color: '#94a3b8' }}>→</div>
                      <div>
                        <div className="text-muted text-sm">Substitute price</div>
                        <strong className="font-mono">£{packSubPopup.newPrice.toFixed(2)}</strong>
                      </div>
                      <div>
                        <div className="text-muted text-sm">Difference</div>
                        <strong className="font-mono" style={{
                          color: packSubPopup.priceDiff > 0 ? '#dc2626' : '#16a34a',
                        }}>
                          {packSubPopup.priceDiff > 0 ? '+' : ''}£{packSubPopup.priceDiff.toFixed(2)}
                        </strong>
                      </div>
                    </div>
                    <p style={{ margin: '8px 0 0', fontSize: 13, color: '#64748b' }}>
                      This could affect the pack price. How would you like to proceed?
                    </p>
                  </>
                )}

                {/* Substitution Reason dropdown - mandatory */}
                <div style={{ margin: '12px 0 16px' }}>
                  <label className="text-sm" style={{ fontWeight: 600 }}>Substitution Reason <span style={{ color: '#dc2626' }}>*</span></label>
                  <select
                    value={selectedReasonId}
                    onChange={e => setSelectedReasonId(e.target.value)}
                    style={{
                      width: '100%', padding: '8px 10px', marginTop: 4, borderRadius: 6,
                      border: '1px solid #cbd5e1', fontSize: 13,
                    }}
                  >
                    <option value="">- Select reason -</option>
                    {subReasons.map(r => (
                      <option key={r.ReasonId} value={r.ReasonId}>{r.Reason}</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  {Math.abs(packSubPopup.priceDiff) > 0.005 ? (
                    <>
                      <button className="hs-btn hs-btn-primary hs-btn-sm" style={{ flex: 1 }}
                        disabled={!selectedReasonId}
                        onClick={() => handlePackSubChoice('accept')}>
                        {packSubPopup.priceDiff > 0 ? '📈 Accept Increase' : '📉 Accept Decrease'}
                      </button>
                      <button className="hs-btn hs-btn-sm hs-btn-secondary" style={{ flex: 1 }}
                        disabled={!selectedReasonId}
                        onClick={() => handlePackSubChoice('keep')}>
                        💰 Keep Pack Price
                      </button>
                    </>
                  ) : (
                    <button className="hs-btn hs-btn-primary hs-btn-sm" style={{ flex: 1 }}
                      disabled={!selectedReasonId}
                      onClick={() => handlePackSubChoice('keep')}>
                      ✓ OK
                    </button>
                  )}
                  <button className="hs-btn hs-btn-sm hs-btn-danger" style={{ flex: 1 }}
                    onClick={() => handlePackSubChoice('cancel')}>
                    ✕ Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ---- Fixed-position product picker dropdown (inline ✏️ edit) ---- */}
          {/* Appears when user clicks the ✏️ button on a standalone line. Filters
              the already-loaded catalog products by name/SKU. Selecting a product
              calls handleChangeLineProduct(). Dismissed on blur (150ms delay
              allows the onMouseDown handler to fire first). */}
          {editingProductLineId && pickerPos && (
            <div style={{
              position: 'fixed', top: pickerPos.top, left: pickerPos.left, width: Math.max(pickerPos.width, 350),
              zIndex: 9999, maxHeight: 260, overflowY: 'auto', background: '#fff',
              border: '1px solid #cbd5e1', borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,.18)',
            }}>
              {(() => {
                const q = productSearchText.toLowerCase();
                // Rank match: 0 = SKU, 1 = Item Name, 2 = Description, -1 = no match
                const rank = p => {
                  if (!q) return 0;
                  if ((p.SKU || '').toLowerCase().includes(q)) return 0;
                  if ((p.ProductName || '').toLowerCase().includes(q)) return 1;
                  if ((p.Description || '').toLowerCase().includes(q)) return 2;
                  return -1;
                };
                const matches = products
                  .map(p => ({ p, rank: rank(p) }))
                  .filter(({ rank }) => rank !== -1)
                  .sort((a, b) => a.rank - b.rank)
                  .map(({ p }) => p);

                if (matches.length === 0) {
                  return (
                    <div style={{ padding: '12px 10px', color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>
                      No products match "{productSearchText}"
                    </div>
                  );
                }

                return matches.slice(0, 50).map(p => (
                  <div key={p.ProductId}
                    style={{
                      padding: '6px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9',
                      background: p.ProductId === (orderLines.find(l => l.LineId === editingProductLineId) || {}).ProductId ? '#fff7ed' : 'transparent',
                    }}
                    onMouseDown={() => {
                      handleChangeLineProduct(editingProductLineId, p.ProductId);
                      setEditingProductLineId(null);
                      setProductSearchText('');
                      setPickerPos(null);
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => {
                      const isCurrentProduct = p.ProductId === (orderLines.find(l => l.LineId === editingProductLineId) || {}).ProductId;
                      e.currentTarget.style.background = isCurrentProduct ? '#fff7ed' : 'transparent';
                    }}
                  >
                    <strong>{p.ProductName}</strong>
                    {p._type === 'pack' && p.PromoPack ? <span className="hs-badge" style={{ background: '#15803d', color: '#fff', fontSize: 9, marginLeft: 4 }}>🎁 Promo</span> : null}
                    <span className="text-muted" style={{ marginLeft: 6, fontSize: 11 }}>
                      {p.SKU} - £{p.UnitPrice.toFixed(2)}
                      {p.StockAtSite ? ` [${p.StockAtSite.AvailableQty} avail]` : ''}
                    </span>
                  </div>
                ));
              })()}
            </div>
          )}

          {/* ======== PRODUCT CATALOG DRAWER (slides in from right) ======== */}
          {/* Fixed to the right half of the viewport; the order lines panel
              shifts its right margin to match so no content is hidden underneath.
              Contains two tabs:
                Catalog  - search/filter product list + "Add" buttons
                Substitute - recommended (Pimberly Sub1/Sub2), algorithm search, browse
              TO ADD A NEW DRAWER TAB: add a button to the tab bar, add a drawerTab
              value, and add a conditional rendering block below. */}
          {showProductPicker && (
              <div style={{
                position: 'fixed', top: 0, right: 0, bottom: 0,
                width: '44vw', maxWidth: '44vw',
                background: '#fff', zIndex: 901,
                boxShadow: '-4px 0 24px rgba(0,0,0,.12)',
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
                borderLeft: '1px solid #e2e8f0',
              }}>
              {/* Search & filter bar - scrollable if tall */}
              <div style={{ flexShrink: 0, borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', gap: 0 }}>
                    <button
                      style={{ padding: '6px 16px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', borderBottom: drawerTab === 'catalog' ? '3px solid var(--hs-orange)' : '3px solid transparent', background: 'none', color: drawerTab === 'catalog' ? 'var(--hs-dark)' : '#64748b' }}
                      onClick={() => setDrawerTab('catalog')}>
                      📋 Catalog ({pagination.totalItems})
                    </button>
                    <button
                      style={{ padding: '6px 16px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', borderBottom: drawerTab === 'substitute' ? '3px solid var(--hs-orange)' : '3px solid transparent', background: 'none', color: drawerTab === 'substitute' ? 'var(--hs-dark)' : '#64748b' }}
                      onClick={() => setDrawerTab('substitute')}>
                      🔄 Substitute {subFinderLineId ? '●' : ''}
                    </button>
                  </div>
                  <button className="hs-btn hs-btn-sm hs-btn-secondary" onClick={() => { setShowProductPicker(false); setDrawerTab('catalog'); resetCatalogFilters(); }}>✕ Close</button>
                </div>
                {drawerTab === 'catalog' && (
                <div style={{ padding: '8px 12px' }}>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                    <input className="hs-input" placeholder="Search…"
                      style={{ flex: 2, padding: '5px 8px', fontSize: 12 }}
                      value={search} onChange={e => setSearch(e.target.value)} />
                    <select className="hs-select" style={{ flex: 1, padding: '5px 4px', fontSize: 12 }}
                      value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                      <option value="all">All</option>
                      <option value="product">Products</option>
                      <option value="pack">Packs</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                    <select className="hs-select" style={{ flex: 1, padding: '5px 4px', fontSize: 12 }}
                      value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                      <option value="">All Categories</option>
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select className="hs-select" style={{ flex: 1, padding: '5px 4px', fontSize: 12 }}
                      value={lifecycleFilter} onChange={e => setLifecycleFilter(e.target.value)}>
                      <option value="">All Stages</option>
                      {lifecycleStages.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <label style={{ fontSize: 11, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <input type="checkbox" checked={inStockOnly}
                        onChange={e => setInStockOnly(e.target.checked)} />
                      In stock
                    </label>
                    <label style={{ fontSize: 11, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 3, color: promoOnly ? '#15803d' : undefined, fontWeight: promoOnly ? 700 : undefined }}>
                      <input type="checkbox" checked={promoOnly}
                        onChange={e => setPromoOnly(e.target.checked)} />
                      🎁 Promo
                    </label>
                  </div>

                  {/* recipe doesn't include filter chips */}
                  {allFreeFromTags.length > 0 && (
                    <div style={{ marginBottom: 4, lineHeight: '22px' }}>
                      <span style={{ fontSize: 10, color: '#64748b', marginRight: 4 }}>Recipe doesn't include:</span>
                      {allFreeFromTags.map(tag => {
                        const sel = selectedFreeFrom.includes(tag);
                        return <button key={tag}
                          style={{ marginRight: 3, marginBottom: 2, padding: '1px 6px', fontSize: 10, border: sel ? 'none' : '1px solid #cbd5e1', borderRadius: 3, background: sel ? '#059669' : '#fff', color: sel ? '#fff' : '#334155', cursor: 'pointer' }}
                          onClick={() => toggleFreeFrom(tag)}>{tag}</button>;
                      })}
                      {selectedFreeFrom.length > 0 && (
                        <button onClick={() => setSelectedFreeFrom([])}
                          style={{ marginLeft: 4, padding: '1px 6px', fontSize: 10, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}>✕ Clear</button>
                      )}
                    </div>
                  )}

                  {/* Dietary filter chips */}
                  {allDietaryTags.length > 0 && (
                    <div style={{ marginBottom: 4, lineHeight: '22px' }}>
                      <span style={{ fontSize: 10, color: '#64748b', marginRight: 4 }}>Dietary:</span>
                      {allDietaryTags.map(tag => {
                        const sel = selectedDietary.includes(tag);
                        return <button key={tag}
                          style={{ marginRight: 3, marginBottom: 2, padding: '1px 6px', fontSize: 10, border: sel ? 'none' : '1px solid #cbd5e1', borderRadius: 3, background: sel ? '#7c3aed' : '#fff', color: sel ? '#fff' : '#334155', cursor: 'pointer' }}
                          onClick={() => toggleDietary(tag)}>{tag}</button>;
                      })}
                      {selectedDietary.length > 0 && (
                        <button onClick={() => setSelectedDietary([])}
                          style={{ marginLeft: 4, padding: '1px 6px', fontSize: 10, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}>✕ Clear</button>
                      )}
                    </div>
                  )}
                </div>
                )}
              </div>

              {/* Product list - unified catalog (products + packs) */}
              {drawerTab === 'catalog' && (<>
              <div style={{ flex: 1, overflow: 'auto' }}>
                  <table className="hs-table" style={{ fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Cat.</th>
                        <th>Info</th>
                        <th className="text-right">Price</th>
                        <th className="text-right">Stock</th>
                        <th style={{ width: 50 }}>Qty</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.length === 0 ? (
                        <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30 }}>
                          <div className="empty-state" style={{ padding: 0 }}>
                            <div className="empty-icon">🔍</div>
                            <h3>No products match your filters</h3>
                            <p>Try adjusting your search or filters</p>
                          </div>
                        </td></tr>
                      ) : products.flatMap(p => {
                        const isPack = p._type === 'pack';
                        const avail = (isStoreMode && !urlStoreId) ? 0 : p.StockAtSite?.AvailableQty;
                        const isExpanded = isPack && expandedPacks.has(p.PackId);

                        const mainRow = (
                          <tr key={p.ProductId} className="clickable"
                            onClick={() => { if (isPack) togglePackExpanded(p.PackId); }}
                            style={{ cursor: isPack ? 'pointer' : 'default' }}>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {isPack && (
                                  <span style={{ fontSize: 12, color: '#64748b', userSelect: 'none' }}>
                                    {isExpanded ? '▼' : '▶'}
                                  </span>
                                )}
                                {p.EStop && (
                                  <span title="On Hold - Cannot be ordered" style={{ fontSize: 12, lineHeight: 1, flexShrink: 0 }}>⛔</span>
                                )}
                                {p.ThumbnailUrl && (
                                  <span
                                    className="tag-hover-trigger"
                                    style={{ cursor: 'default', flexShrink: 0 }}
                                    onMouseEnter={e => {
                                      const popup = e.currentTarget.querySelector('.thumb-popup');
                                      if (!popup) return;
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      if (window.innerHeight - rect.bottom < 320) {
                                        popup.style.top = 'auto'; popup.style.bottom = 'calc(100% + 4px)';
                                      } else {
                                        popup.style.top = 'calc(100% + 4px)'; popup.style.bottom = 'auto';
                                      }
                                    }}
                                  >
                                    <img src={p.ThumbnailUrl} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 3, verticalAlign: 'middle', display: 'block' }} />
                                    {p.ImageUrl && (
                                      <span className="thumb-popup">
                                        <img src={p.ImageUrl} alt="" style={{ width: 300, height: 'auto', display: 'block' }} />
                                      </span>
                                    )}
                                  </span>
                                )}
                                <div>
                                  <strong>
                                    {isPack && <span title="Pack" style={{ marginRight: 4 }}>📦</span>}
                                    {p.ProductName}
                                  </strong>
                                  {isPack && p.PromoPack ? (
                                    <div style={{ marginTop: 2 }}>
                                      <span className="hs-badge" style={{ background: '#15803d', color: '#fff', fontSize: 9 }}>🎁 Promo</span>
                                    </div>
                                  ) : null}
                                  <div className="text-muted text-sm font-mono">{p.SKU}</div>
                                  <div className="text-muted text-sm" style={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {isPack ? `${(p.Items || []).length} items` : p.Description}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={`hs-badge ${isPack ? 'hs-badge-pack' : 'hs-badge-draft'}`}
                                style={isPack ? { background: '#7c3aed', color: '#fff' } : {}}>
                                {p.Category}
                              </span>
                            </td>
                            <td>
                              {!isPack ? (
                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                  {p.ProductNotes && (
                                    <span className="tag-hover-trigger" onMouseEnter={positionPopup}>
                                      <span style={{ fontSize: 14, cursor: 'help' }}>⚠️</span>
                                      <span className="tag-hover-popup" style={{ maxWidth: 280, whiteSpace: 'normal' }}>
                                        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Product Note</div>
                                        <div style={{ fontSize: 11 }}>{p.ProductNotes}</div>
                                      </span>
                                    </span>
                                  )}
                                  {(p.FreeFrom || []).length > 0 && (
                                    <span className="tag-hover-trigger" onMouseEnter={positionPopup}>
                                      <span className="hs-badge" style={{ background: '#059669', color: '#fff', fontSize: 10 }}>✓ {p.FreeFrom.length}</span>
                                      <span className="tag-hover-popup">
                                        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Recipe doesn't include</div>
                                        {p.FreeFrom.map(tag => (
                                          <span key={tag} className="hs-badge" style={{ background: '#059669', color: '#fff', marginRight: 3, marginBottom: 2, fontSize: 10 }}>✓ {tag}</span>
                                        ))}
                                      </span>
                                    </span>
                                  )}
                                  {(p.Dietary || []).length > 0 && (
                                    <span className="tag-hover-trigger" onMouseEnter={positionPopup}>
                                      <span className="hs-badge" style={{ background: '#7c3aed', color: '#fff', fontSize: 10 }}>🌱 {p.Dietary.length}</span>
                                      <span className="tag-hover-popup">
                                        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Dietary</div>
                                        {p.Dietary.map(tag => (
                                          <span key={tag} className="hs-badge" style={{ background: '#7c3aed', color: '#fff', marginRight: 3, marginBottom: 2, fontSize: 10 }}>🌱 {tag}</span>
                                        ))}
                                      </span>
                                    </span>
                                  )}
                                </div>
                              ) : (() => {
                                const items = (p.Items || []).filter(i => i.Product);
                                if (!items.length) return null;
                                const allFF = items.map(i => new Set(i.Product.FreeFrom || []));
                                const commonFF = [...allFF[0]].filter(t => allFF.every(s => s.has(t)));
                                const allDT = items.map(i => new Set(i.Product.Dietary || []));
                                const commonDT = [...allDT[0]].filter(t => allDT.every(s => s.has(t)));
                                return (
                                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                    {p.ProductNotes && (
                                      <span className="tag-hover-trigger" onMouseEnter={positionPopup}>
                                        <span style={{ fontSize: 14, cursor: 'help' }}>⚠️</span>
                                        <span className="tag-hover-popup" style={{ maxWidth: 280, whiteSpace: 'normal' }}>
                                          <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Product Note</div>
                                          <div style={{ fontSize: 11 }}>{p.ProductNotes}</div>
                                        </span>
                                      </span>
                                    )}
                                    {commonFF.length > 0 && (
                                      <span className="tag-hover-trigger" onMouseEnter={positionPopup}>
                                        <span className="hs-badge" style={{ background: '#059669', color: '#fff', fontSize: 10 }}>✓ {commonFF.length}</span>
                                        <span className="tag-hover-popup">
                                          <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Pack Recipe Doesn't Include (all items)</div>
                                          {commonFF.map(tag => (
                                            <span key={tag} className="hs-badge" style={{ background: '#059669', color: '#fff', marginRight: 3, marginBottom: 2, fontSize: 10 }}>✓ {tag}</span>
                                          ))}
                                        </span>
                                      </span>
                                    )}
                                    {commonDT.length > 0 && (
                                      <span className="tag-hover-trigger" onMouseEnter={positionPopup}>
                                        <span className="hs-badge" style={{ background: '#7c3aed', color: '#fff', fontSize: 10 }}>🌱 {commonDT.length}</span>
                                        <span className="tag-hover-popup">
                                          <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Pack Dietary (all items)</div>
                                          {commonDT.map(tag => (
                                            <span key={tag} className="hs-badge" style={{ background: '#7c3aed', color: '#fff', marginRight: 3, marginBottom: 2, fontSize: 10 }}>🌱 {tag}</span>
                                          ))}
                                        </span>
                                      </span>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="text-right font-mono">£{(p.UnitPrice || 0).toFixed(2)}</td>
                            <td className="text-right">
                              {avail !== undefined
                                ? <strong className={avail > 0 ? 'text-success' : 'text-danger'}>{avail}</strong>
                                : <span className="text-muted">-</span>
                              }
                            </td>
                            <td>
                              <input type="number" min="1"
                                style={{ width: 44, padding: '2px 4px', fontSize: 12, textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: 4 }}
                                value={rowQtys[p.ProductId] ?? ''}
                                placeholder="1"
                                onClick={e => e.stopPropagation()}
                                onChange={e => setRowQtys(prev => ({ ...prev, [p.ProductId]: e.target.value }))} />
                            </td>
                            <td>
                              {p.EStop
                                ? <button className="hs-btn hs-btn-sm" disabled title="⛔ On Hold - Cannot be ordered" style={{ opacity: 0.55, cursor: 'not-allowed', background: '#dc2626', color: '#fff', border: 'none' }}>⛔ On Hold</button>
                                : <button className="hs-btn hs-btn-sm hs-btn-primary" onClick={(e) => { e.stopPropagation(); handleAddProduct(p, parseInt(rowQtys[p.ProductId]) || 1); resetCatalogFilters(); }}>+ Add</button>
                              }
                            </td>
                          </tr>
                        );

                        // Nested child rows for expanded packs
                        const childRows = isExpanded ? (p.Items || []).map((item, idx) => {
                          const cp = item.Product;
                          return (
                            <tr key={`${p.ProductId}-item-${idx}`}
                              style={{ background: '#f8fafc', fontSize: 13 }}>
                              <td style={{ paddingLeft: 44 }}>
                                <span style={{ color: '#94a3b8', marginRight: 6 }}>└</span>
                                <strong>{cp?.ProductName || 'Unknown'}</strong>
                                {cp?.PromoPack ? <span className="hs-badge" style={{ background: '#15803d', color: '#fff', fontSize: 9, marginLeft: 4 }}>🎁 Promo</span> : null}
                                <span className="text-muted text-sm" style={{ marginLeft: 8 }}>×{item.Quantity}</span>
                                <div className="text-muted text-sm font-mono" style={{ paddingLeft: 18 }}>{cp?.SKU || ''}</div>
                              </td>
                              <td><span className="hs-badge hs-badge-draft" style={{ fontSize: 10 }}>{cp?.Category || ''}</span></td>
                              <td>
                                <div style={{ display: 'flex', gap: 3 }}>
                                  {(cp?.FreeFrom || []).length > 0 && (
                                    <span className="tag-hover-trigger" onMouseEnter={positionPopup}>
                                      <span className="hs-badge" style={{ background: '#059669', color: '#fff', fontSize: 9 }}>✓ {cp.FreeFrom.length}</span>
                                      <span className="tag-hover-popup">
                                        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Recipe Doesn't Include</div>
                                        {cp.FreeFrom.map(tag => (
                                          <span key={tag} className="hs-badge" style={{ background: '#059669', color: '#fff', marginRight: 2, marginBottom: 1, fontSize: 9 }}>✓ {tag}</span>
                                        ))}
                                      </span>
                                    </span>
                                  )}
                                  {(cp?.Dietary || []).length > 0 && (
                                    <span className="tag-hover-trigger" onMouseEnter={positionPopup}>
                                      <span className="hs-badge" style={{ background: '#7c3aed', color: '#fff', fontSize: 9 }}>🌱 {cp.Dietary.length}</span>
                                      <span className="tag-hover-popup">
                                        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Dietary</div>
                                        {cp.Dietary.map(tag => (
                                          <span key={tag} className="hs-badge" style={{ background: '#7c3aed', color: '#fff', marginRight: 2, marginBottom: 1, fontSize: 9 }}>🌱 {tag}</span>
                                        ))}
                                      </span>
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="text-right font-mono text-muted">£{(cp?.UnitPrice || 0).toFixed(2)}</td>
                              <td className="text-right">
                                {(() => {
                                  const childAvail = (isStoreMode && !urlStoreId) ? 0 : cp?.StockAtSite?.AvailableQty;
                                  return childAvail !== undefined && childAvail !== null
                                    ? <strong className={childAvail > 0 ? 'text-success' : 'text-danger'} style={{ fontSize: 12 }}>{childAvail}</strong>
                                    : <span className="text-muted">-</span>;
                                })()}
                              </td>
                              <td></td>
                              <td></td>
                            </tr>
                          );
                        }) : [];

                        return [mainRow, ...childRows];
                      })}
                    </tbody>
                  </table>
              </div>
              {/* Pagination controls */}
              {pagination.totalPages > 1 && (
                <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, padding: '12px 16px', borderTop: '1px solid #e2e8f0' }}>
                  <button className="hs-btn hs-btn-sm hs-btn-secondary" disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}>← Prev</button>
                  <span className="text-sm">Page {page} of {pagination.totalPages}</span>
                  <button className="hs-btn hs-btn-sm hs-btn-secondary" disabled={page >= pagination.totalPages}
                    onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}>Next →</button>
                </div>
              )}
              </>
              )}

              {/* ======== SUBSTITUTE TAB ======== */}
              {drawerTab === 'substitute' && (() => {
                const subLine = orderLines.find(l => l.LineId === subFinderLineId);
                const subProd = subLine?.Product;
                const isPackChild = subLine && !!subLine.ParentPackCode;
                return (
                  <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
                    {!subLine ? (
                      <div className="empty-state" style={{ textAlign: 'center', padding: 40 }}>
                        <div className="empty-icon">🔄</div>
                        <h3>No product selected</h3>
                        <p className="text-muted">Click the 🔄 button on an order line to find substitutes.</p>
                      </div>
                    ) : (
                      <>
                        <div style={{ marginBottom: 12 }}>
                          <h3 style={{ margin: 0, fontSize: 15 }}>
                            🔄 Substitute for <span style={{ color: 'var(--hs-orange)' }}>{subProd?.ProductName || '-'}</span>
                          </h3>
                          {isPackChild && (
                            <span className="hs-badge hs-badge-pack" style={{ marginTop: 4, fontSize: 10, display: 'inline-block' }}>
                              Pack child - original kept, sub stored as code
                            </span>
                          )}
                          {subProd && (
                            <div className="text-muted text-sm" style={{ marginTop: 4 }}>
                              {subProd.SKU} - £{subProd.UnitPrice?.toFixed(2)}
                              {subLine.StockAtSite ? ` - Stock: ${subLine.StockAtSite.AvailableQty}` : ''}
                            </div>
                          )}
                        </div>

                        {/* Inner tabs: Recommended | Search */}
                        <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', marginBottom: 12 }}>
                          <button
                            style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', borderBottom: subInnerTab === 'recommended' ? '2px solid var(--hs-orange)' : '2px solid transparent', marginBottom: -2, background: 'none', color: subInnerTab === 'recommended' ? 'var(--hs-dark)' : '#64748b' }}
                            onClick={() => setSubInnerTab('recommended')}>
                            ⭐ Recommended ({recommendedSubs ? recommendedSubs.length : '…'})
                          </button>
                          <button
                            style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', borderBottom: subInnerTab === 'search' ? '2px solid var(--hs-orange)' : '2px solid transparent', marginBottom: -2, background: 'none', color: subInnerTab === 'search' ? 'var(--hs-dark)' : '#64748b' }}
                            onClick={() => setSubInnerTab('search')}>
                            🔍 Search
                          </button>
                          <button
                            style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', borderBottom: subInnerTab === 'browse' ? '2px solid var(--hs-orange)' : '2px solid transparent', marginBottom: -2, background: 'none', color: subInnerTab === 'browse' ? 'var(--hs-dark)' : '#64748b' }}
                            onClick={() => setSubInnerTab('browse')}>
                            📋 Browse All
                          </button>
                        </div>

                        {/* ---- RECOMMENDED TAB ---- */}
                        {subInnerTab === 'recommended' && (
                          <div>
                            {recommendedLoading ? (
                              <p className="text-muted text-sm">Loading recommended substitutes…</p>
                            ) : !recommendedSubs || recommendedSubs.length === 0 ? (
                              <div style={{ textAlign: 'center', padding: 20 }}>
                                <p className="text-muted text-sm">No recommended substitutes (Sub1/Sub2) configured in Pimberly for this product.</p>
                                <button className="hs-btn hs-btn-sm hs-btn-secondary" style={{ marginTop: 8 }} onClick={() => setSubInnerTab('search')}>
                                  🔍 Search for alternatives
                                </button>
                              </div>
                            ) : (
                              <table className="hs-table" style={{ fontSize: 12 }}>
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Product</th>
                                    <th>SKU</th>
                                    <th className="text-right">Price</th>
                                    <th className="text-right">Stock</th>
                                    <th></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {recommendedSubs.map(rec => {
                                    const sp = rec.SubstituteProduct;
                                    const priceDiff = sp?.UnitPrice && subProd?.UnitPrice ? sp.UnitPrice - subProd.UnitPrice : null;
                                    return (
                                      <tr key={rec.SubstituteCode} style={sp?._pimberlyOnly ? { opacity: 0.6 } : {}}>
                                        <td>
                                          <span className="hs-badge hs-badge-available" style={{ fontSize: 10 }}>Sub{rec.Priority}</span>
                                        </td>
                                        <td>
                                          <strong>{sp?.ProductName || rec.SubstituteCode}</strong>
                                          {sp?.Description && <div className="text-muted text-sm">{sp.Description.substring(0, 60)}…</div>}
                                          {sp?._pimberlyOnly && <div className="text-muted text-sm" style={{ fontStyle: 'italic' }}>Pimberly only - not in product catalogue</div>}
                                        </td>
                                        <td><span className="font-mono text-sm">{sp?.SKU || rec.SubstituteCode}</span></td>
                                        <td className="text-right">
                                          <span className="font-mono">£{(sp?.UnitPrice || 0).toFixed(2)}</span>
                                          {priceDiff != null && priceDiff !== 0 && (
                                            <div className={priceDiff > 0 ? 'text-danger' : 'text-success'} style={{ fontSize: 10 }}>
                                              {priceDiff > 0 ? '+' : ''}£{priceDiff.toFixed(2)}
                                            </div>
                                          )}
                                        </td>
                                        <td className="text-right">
                                          {rec.StockAtSite ? (
                                            <strong className={rec.StockAtSite.AvailableQty > 0 ? 'text-success' : 'text-danger'}>
                                              {rec.StockAtSite.AvailableQty}
                                            </strong>
                                          ) : <span className="text-muted">-</span>}
                                        </td>
                                        <td>
                                          {sp?.ProductId && !sp._pimberlyOnly ? (
                                            <button className="hs-btn hs-btn-sm hs-btn-primary"
                                              onClick={() => handlePickSubstitute(subFinderLineId, sp)}>
                                              {isPackChild ? 'Set Sub' : 'Use This'}
                                            </button>
                                          ) : (
                                            <span className="text-muted text-sm">N/A</span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}

                        {/* ---- SEARCH TAB ---- */}
                        {subInnerTab === 'search' && (
                          <div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                          <div>
                            <label className="text-sm" style={{ fontSize: 11 }}>Max Price Δ (£)</label>
                            <input className="hs-input" type="number" min="0" step="0.5" style={{ width: 80, padding: '4px 6px', fontSize: 12 }}
                              value={subCriteria.maxPriceDelta}
                              onChange={e => setSubCriteria({ ...subCriteria, maxPriceDelta: parseFloat(e.target.value) || 0 })} />
                          </div>
                          <div>
                            <label className="text-sm" style={{ fontSize: 11 }}>Category</label>
                            <select className="hs-select" style={{ minWidth: 120, padding: '4px 6px', fontSize: 12 }} value={subCriteria.category}
                              onChange={e => setSubCriteria({ ...subCriteria, category: e.target.value })}>
                              <option value="">Any</option>
                              {categories.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-sm" style={{ fontSize: 11 }}>Match Ingredients</label>
                            <input className="hs-input" style={{ minWidth: 160, padding: '4px 6px', fontSize: 12 }} placeholder="e.g. chicken, cheese"
                              value={subCriteria.matchIngredients.join(', ')}
                              onChange={e => setSubCriteria({
                                ...subCriteria,
                                matchIngredients: e.target.value ? e.target.value.split(',').map(i => i.trim()).filter(Boolean) : []
                              })} />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                            <label className="text-sm" style={{ fontSize: 11 }}>
                              <input type="checkbox" checked={subCriteria.inStockOnly}
                                onChange={e => setSubCriteria({ ...subCriteria, inStockOnly: e.target.checked })}
                                style={{ marginRight: 4 }} />
                              In stock
                            </label>
                          </div>
                        </div>

                        {/* Allergens to avoid */}
                        <div style={{ marginBottom: 12 }}>
                          <span className="text-muted text-sm" style={{ fontSize: 11 }}>Avoid allergens:</span>
                          <div style={{ marginTop: 4 }}>
                            {Object.keys(ALLERGEN_COLOURS).map(a => (
                              <button key={a}
                                style={{ marginRight: 3, marginBottom: 3, padding: '2px 8px', fontSize: 11, border: subCriteria.avoidAllergens.includes(a) ? 'none' : '1px solid #cbd5e1', borderRadius: 3, background: subCriteria.avoidAllergens.includes(a) ? '#dc2626' : '#fff', color: subCriteria.avoidAllergens.includes(a) ? '#fff' : '#334155', cursor: 'pointer' }}
                                onClick={() => toggleSubAllergen(a)}>
                                {subCriteria.avoidAllergens.includes(a) ? '✕ ' : ''}{a}
                              </button>
                            ))}
                          </div>
                        </div>

                        <button className="hs-btn hs-btn-primary hs-btn-sm" onClick={() => handleFindSubs(subProd.ProductId)} disabled={subSearching || !subProd}>
                          {subSearching ? 'Searching…' : '🔍 Find Matches'}
                        </button>

                        {/* Results table */}
                        {subResults && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                              <span className="text-sm" style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{subResults.totalCandidates} substitute{subResults.totalCandidates !== 1 ? 's' : ''} found</span>
                              <input className="hs-input" style={{ flex: 1, fontSize: 12, padding: '4px 8px' }}
                                placeholder="Filter by name or code…"
                                value={subResultsFilter}
                                onChange={e => setSubResultsFilter(e.target.value)} />
                            </div>
                            {subResults.totalCandidates === 0 ? (
                              <p className="text-muted text-sm" style={{ margin: '8px 0 0' }}>
                                No matches. Try widening price range or removing allergen filters.
                              </p>
                            ) : (() => {
                              const q = subResultsFilter.toLowerCase();
                              const filtered = subResults.substitutes.filter(s =>
                                !q || (s.ProductName || '').toLowerCase().includes(q) || (s.SKU || '').toLowerCase().includes(q)
                              );
                              return (
                              <div style={{ overflowX: 'auto' }}>
                                <table className="hs-table" style={{ fontSize: 12, width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr>
                                      <th style={{ width: 42, textAlign: 'center', padding: '6px 4px', cursor: 'help' }} title={"Relevance score based on:\n• Same category: +30\n• Price similarity: up to +20\n• Ingredient overlap: up to +20\n• Fewer allergens: +10 (+5 if none)\n• In stock: +10\n• Substitution rule exists: +15"}>Match (?)</th>
                                      <th style={{ padding: '6px 8px' }}>Product</th>
                                      <th style={{ width: 56, textAlign: 'center', padding: '6px 4px' }}>Code</th>
                                      <th style={{ width: 52, textAlign: 'right', padding: '6px 6px' }}>Price</th>
                                      <th style={{ width: 52, textAlign: 'right', padding: '6px 6px' }}>Δ</th>
                                      <th style={{ width: 42, textAlign: 'center', padding: '6px 4px' }}>Stock</th>
                                      <th style={{ width: 58, padding: '6px 4px' }}></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filtered.map(s => (
                                      <tr key={s.ProductId} style={{ verticalAlign: 'middle' }}>
                                        <td style={{ textAlign: 'center', padding: '5px 4px' }}>
                                          <span className={`hs-badge ${s.RelevanceScore >= 60 ? 'hs-badge-available' : s.RelevanceScore >= 30 ? 'hs-badge-pending' : 'hs-badge-draft'}`} style={{ fontSize: 10 }}>
                                            {s.RelevanceScore}%
                                          </span>
                                        </td>
                                        <td style={{ padding: '5px 8px', maxWidth: 0 }} title={`${s.ProductName}\n${s.Description || ''}`}>
                                          <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.ProductName}</div>
                                          <div className="text-muted" style={{ fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.Description?.substring(0, 80)}</div>
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '5px 4px', fontSize: 11 }}>{s.SKU}</td>
                                        <td className="font-mono" style={{ textAlign: 'right', padding: '5px 6px' }}>£{s.UnitPrice.toFixed(2)}</td>
                                        <td style={{ textAlign: 'right', padding: '5px 6px' }}>
                                          <span className={s.PriceDifference > 0 ? 'text-danger' : s.PriceDifference < 0 ? 'text-success' : ''}>
                                            {s.PriceDifference > 0 ? '+' : ''}{s.PriceDifference !== 0 ? `£${s.PriceDifference.toFixed(2)}` : '-'}
                                          </span>
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '5px 4px' }}>
                                          {s.StockAtSite ? (
                                            <strong className={s.StockAtSite.AvailableQty > 0 ? 'text-success' : 'text-danger'}>
                                              {s.StockAtSite.AvailableQty}
                                            </strong>
                                          ) : '-'}
                                        </td>
                                        <td style={{ padding: '5px 4px' }}>
                                          <button className="hs-btn hs-btn-sm hs-btn-primary" style={{ fontSize: 11, padding: '3px 8px', whiteSpace: 'nowrap' }}
                                            onClick={() => handlePickSubstitute(subFinderLineId, s)}>
                                            {isPackChild ? 'Set Sub' : 'Use'}
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                    {filtered.length === 0 && (
                                      <tr><td colSpan={7} className="text-muted text-sm" style={{ textAlign: 'center', padding: 12 }}>No results match "{subResultsFilter}"</td></tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            );})()}
                          </div>
                        )}
                          </div>
                        )}

                        {/* ---- BROWSE ALL TAB ---- */}
                        {subInnerTab === 'browse' && (() => {
                          const handleBrowseSearch = async () => {
                            if (!browseSearch.trim()) return;
                            setBrowseLoading(true);
                            try {
                              const res = await getProducts({ search: browseSearch.trim(), siteId: order?.SiteId, showType: 'product' });
                              setBrowseResults((res.data.data || []).filter(p => p._type !== 'pack'));
                            } catch (e) {
                              console.error(e);
                              setBrowseResults([]);
                            } finally {
                              setBrowseLoading(false);
                            }
                          };
                          return (
                            <div>
                              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                                <input className="hs-input" style={{ flex: 1, fontSize: 12, padding: '5px 8px' }}
                                  placeholder="Search by name, SKU, or description…"
                                  value={browseSearch}
                                  onChange={e => setBrowseSearch(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') handleBrowseSearch(); }} />
                                <button className="hs-btn hs-btn-sm hs-btn-primary" onClick={handleBrowseSearch} disabled={browseLoading || !browseSearch.trim()}>
                                  {browseLoading ? '…' : '🔍'}
                                </button>
                              </div>
                              {browseResults && browseResults.length === 0 && (
                                <p className="text-muted text-sm">No products found for "{browseSearch}".</p>
                              )}
                              {browseResults && browseResults.length > 0 && (
                                <div style={{ overflowX: 'auto' }}>
                                  <span className="text-sm" style={{ fontWeight: 600, marginBottom: 6, display: 'block' }}>{browseResults.length} product{browseResults.length !== 1 ? 's' : ''} found</span>
                                  <table className="hs-table" style={{ fontSize: 12, width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                      <tr>
                                        <th style={{ padding: '6px 8px' }}>Product</th>
                                        <th style={{ width: 56, textAlign: 'center', padding: '6px 4px' }}>Code</th>
                                        <th style={{ width: 52, textAlign: 'right', padding: '6px 6px' }}>Price</th>
                                        <th style={{ width: 52, textAlign: 'right', padding: '6px 6px' }}>Δ</th>
                                        <th style={{ width: 42, textAlign: 'center', padding: '6px 4px' }}>Stock</th>
                                        <th style={{ width: 58, padding: '6px 4px' }}></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {browseResults.filter(bp => bp.ProductId !== subProd?.ProductId).map(bp => {
                                        const priceDiff = subProd?.UnitPrice ? bp.UnitPrice - subProd.UnitPrice : null;
                                        const bpStock = bp.StockAtSite?.AvailableQty;
                                        return (
                                          <tr key={bp.ProductId} style={{ verticalAlign: 'middle' }}>
                                            <td style={{ padding: '5px 8px', maxWidth: 0 }} title={`${bp.ProductName}\n${bp.Description || ''}`}>
                                              <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{bp.ProductName}</div>
                                              <div className="text-muted" style={{ fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{bp.Description?.substring(0, 80)}</div>
                                            </td>
                                            <td style={{ textAlign: 'center', padding: '5px 4px', fontSize: 11 }}>{bp.SKU}</td>
                                            <td className="font-mono" style={{ textAlign: 'right', padding: '5px 6px' }}>£{(bp.UnitPrice || 0).toFixed(2)}</td>
                                            <td style={{ textAlign: 'right', padding: '5px 6px' }}>
                                              {priceDiff != null && priceDiff !== 0 ? (
                                                <span className={priceDiff > 0 ? 'text-danger' : 'text-success'}>
                                                  {priceDiff > 0 ? '+' : ''}£{priceDiff.toFixed(2)}
                                                </span>
                                              ) : <span>-</span>}
                                            </td>
                                            <td style={{ textAlign: 'center', padding: '5px 4px' }}>
                                              {bpStock !== undefined ? (
                                                <strong className={bpStock > 0 ? 'text-success' : 'text-danger'}>{bpStock}</strong>
                                              ) : <span className="text-muted">-</span>}
                                            </td>
                                            <td style={{ padding: '5px 4px' }}>
                                              <button className="hs-btn hs-btn-sm hs-btn-primary" style={{ fontSize: 11, padding: '3px 8px', whiteSpace: 'nowrap' }}
                                                onClick={() => handlePickSubstitute(subFinderLineId, bp)}>
                                                {isPackChild ? 'Set Sub' : 'Use'}
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                );
              })()}

              </div>
          )}
        </div>
      </div>

      {/* ---- SUBMIT CONFIRMATION MODAL (store/popup mode only) ---- */}
      {/* Shown after a successful submit in isStoreMode.
          User can check "Don't show this again" - stored in localStorage.
          Clicking OK attempts window.close() to dismiss the HubSpot popup. */}
      {showSubmitConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '28px 32px',
            maxWidth: 420, width: '90%', boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>Order Submitted</h3>
            <p style={{ margin: '0 0 20px', fontSize: 14, lineHeight: 1.5 }}>
              Please go back to the opened order in HubSpot and refresh the page in your web browser.
            </p>
            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 13, marginBottom: 20, cursor: 'pointer' }}>
              <input
                type="checkbox"
                id="hideSubmitConfirmCb"
              />
              Don't show this message again
            </label>
            <button
              style={{
                padding: '8px 32px', fontSize: 14, borderRadius: 4,
                border: 'none', background: '#ff7a59', color: '#fff',
                cursor: 'pointer', fontWeight: 600
              }}
              onClick={() => {
                const cb = document.getElementById('hideSubmitConfirmCb');
                if (cb && cb.checked) {
                  localStorage.setItem('hideSubmitConfirm', '1');
                }
                setShowSubmitConfirm(false);
                setTimeout(() => {
                  try { window.close(); } catch (_) {}
                  setStatusMsg({ type: 'success', text: 'Order submitted! You can close this window.' });
                }, 500);
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
}
